import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box, Typography, Paper, Stack, Divider, Table,
  TableBody, TableCell, TableHead, TableRow, Button, Chip, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, InputAdornment, Avatar, Tooltip, Menu, MenuItem, IconButton,
  FormControlLabel, Checkbox, Select,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import LinkIcon from '@mui/icons-material/Link';
import SearchIcon from '@mui/icons-material/Search';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import WorkIcon from '@mui/icons-material/Work';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EmailIcon from '@mui/icons-material/Email';
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CloseIcon from '@mui/icons-material/Close';
import {
  autoMatchInternalCode, autoMatchOrCreate, classifyImportLine, generateInternalCode,
  mintInternalCode, peekNextInternalCode, needsInternalCode, guessLineType, type ImportLineState, type ImportUserAction,
} from '../utils/codeMatching';
import {
  InfoableButton,
} from '../components/ImportResolutionBadge';
import type { ButtonInfo } from '../components/ImportResolutionBadge';
import { useNavigate, useParams } from 'react-router-dom';
import { useData } from '../context/DataContext';
import type { BillLineItem, PoStatus } from '../data/types';
import { PO_STATUSES } from '../data/types';
import { BRAND, STATUS_TONE, getStatusTone } from '../theme/tokens';
import {
  BTN_STATUS_GREEN, BTN_STATUS_ORANGE, BTN_PURPLE, BTN_STANDARD, BTN_GHOST_DOC,
  BTN_GHOST_DANGER, LINK_BLUE, TABLE_HEAD_CELL, TABLE_HEAD_SUPPLIER,
  PO_COLORS, PO_STICKY_LIFECYCLE_SX, PO_STICKY_LIFECYCLE_PUSH, PO_STICKY_LIFECYCLE_TRAIL,
} from '../theme/poButtonTokens';
import { StatusChip } from '../components/StatusChip';
import { POEditForm } from '../components/POEditForm';
import { AiImportBanner } from '../components/AiImportBanner';
import { setPendingBillImportFile } from '../utils/billImportHandoff';
import { AttachFileZone } from '../components/AttachFileZone';
import { SupplierImportPreviewDialog } from '../components/SupplierImportPreviewDialog';
import { DocumentPreviewRail } from '../components/DocumentPreviewRail';
import { CollapseDivider, COLLAPSE_THUMB_WIDTH } from '../components/CollapseDivider';
import { LineCodeSelect } from '../components/LineCodeSelect';
import type { LineCodeOption } from '../components/LineCodeSelect';
import {
  LineTableColumnSettings, loadColumnVisibility, saveColumnVisibility,
} from '../components/LineTableColumnSettings';
import { PO_LINE_COLUMN_DEFS } from '../config/salesDocumentLineColumns';
import { AddProductDialog } from '../components/AddProductDialog';
import { AddSupplierCodeDialog } from '../components/AddSupplierCodeDialog';
import type { SupplierCodeDraft } from '../components/AddSupplierCodeDialog';
import { ResolveCodeDialog } from '../components/ResolveCodeDialog';
import type { AddProductDraft } from '../components/AddProductDialog';

const FI_ORANGE = '#F57C00';
const PO_TEMPLATES = ['Original Purchase Order', 'Service Engagement', 'Subcontractor Agreement'];

const PO_LINE_COL_IDS = PO_LINE_COLUMN_DEFS.map(c => c.id);

// ── Import-preview footer button explanations (v1.22, apply-tooltip-pattern) ─
// Hover any footer button to reveal a `?` glyph; clicking it opens a dialog
// with these Description + Example sections (real data shapes).
// Footer doc-tool explanations (v1.27.4 — apply-tooltip-pattern on ghost row)
// v1.27.20 — extended to every workflow button: Bills row + lifecycle row all
// carry the click-? affordance; hover-only tooltips removed.
const FOOTER_BTN_INFO: Record<
  | 'preview' | 'download' | 'email' | 'copy' | 'createJob' | 'createCredit' | 'useCredit' | 'exportXero'
  | 'reconcileInvoice' | 'findBill' | 'generateBillFromPO' | 'generateBlankBill'
  | 'close' | 'submit' | 'approve' | 'approveEmail' | 'finalise' | 'receiveAll' | 'reopenDraft' | 'deletePo',
  ButtonInfo
> = {
  reconcileInvoice: {
    title: 'Reconcile Supplier Invoice',
    description: 'Opens the reconciliation workspace on this PO. Drop the supplier invoice on the AI import area — it creates a new bill, reads the lines, and opens reconciliation. Or click an existing bill in the table to reconcile that one.',
    example: 'Quantum Firetech emails their invoice for PO-0039. Reconcile Supplier Invoice opens the workspace; you drag the PDF onto Supplier Invoice Import and AI parses it into a new bill matched against the PO.',
  },
  findBill: {
    title: '+ Find Bill',
    description: 'Opens a full-page search to link an existing bill in the system to this PO — by bill #, supplier, invoice #, or job.',
    example: 'BILL-2026-019 was created from a scanned invoice last week. + Find Bill opens the search page and links it to PO-0039.',
  },
  generateBillFromPO: {
    title: '+ Generate Bill from PO',
    description: 'Creates a draft bill with this PO\u2019s lines copied in and opens the full bill page — quantities, prices, and codes pre-filled.',
    example: 'PO-0039 has 4 lines totalling $661.17. Generate Bill from PO opens BILL-2026-0XX with all 4 lines ready to adjust against the supplier invoice.',
  },
  generateBlankBill: {
    title: '+ New bill',
    description: 'Creates an empty draft bill linked to this PO and opens it on the bill page — use for freight, surcharges, or manual entry from scratch.',
    example: 'The supplier\u2019s invoice adds a $45 freight line that isn\u2019t on PO-0039. New bill opens an empty bill linked to the PO to build by hand.',
  },
  close: {
    title: 'Close',
    description: 'Leaves this purchase order and returns to the PO list. Nothing is saved or discarded — edits commit as you make them.',
    example: 'You\u2019ve checked PO-0039\u2019s received quantities; Close takes you back to the list to work the next order.',
  },
  submit: {
    title: 'Submit',
    description: 'Sends this draft PO for approval. Used when the PO total is outside your approval limit — an approver reviews it before it can be sent to the supplier.',
    example: 'PO-0039 is $661.17 and your limit is $500. Submit moves it to Submitted; your manager sees it in their approvals queue.',
  },
  approve: {
    title: 'Approve',
    description: 'Approves this submitted PO so it can be sent to the supplier and received against. The status verb — the strongest action on the page.',
    example: 'Your manager reviews PO-0039\u2019s lines and total, hits Approve, and the order is live.',
  },
  approveEmail: {
    title: 'Approve & Email',
    description: 'Approves the PO and immediately emails it to the supplier in one step — the common path when approval and sending happen together.',
    example: 'Approve & Email on PO-0039 sets it to Approved and sends the PDF to accounts@quantumfiretech.com.au in one click.',
  },
  finalise: {
    title: 'Finalise',
    description: 'Closes out this PO — goods received and the supplier\u2019s invoice reconciled. A finalised PO is read-only; reopen it as Draft if something changes.',
    example: 'All 4 lines on PO-0039 are received and BILL-2026-019 matches the order. Finalise locks the PO and removes it from the open-orders list.',
  },
  receiveAll: {
    title: 'Receive all',
    description: 'Sets Received Qty to the full ordered quantity on every line — a one-click shortcut when the whole delivery arrived intact. It does not create a bill.',
    example: 'The Quantum Firetech delivery arrives complete. Receive all marks 4 of 4 lines received; the bill is reconciled separately when the invoice lands.',
  },
  reopenDraft: {
    title: 'Reopen as Draft',
    description: 'Reopens a finalised or cancelled PO back to Draft so it can be edited again. Use sparingly — the audit trail notes the reopen.',
    example: 'PO-0039 was finalised but the supplier issued a corrected invoice. Reopen as Draft lets you adjust the lines and re-finalise.',
  },
  deletePo: {
    title: 'Delete',
    description: 'Cancels this draft PO permanently. Only available while the PO is a Draft — once submitted or approved, cancel instead so the audit trail survives.',
    example: 'PO-0040 was raised against the wrong supplier. Delete removes the draft; nothing was ever sent or received.',
  },
  preview: {
    title: 'Preview',
    description: 'Opens a read-only view of this purchase order exactly as the supplier will see it — letterhead, line items, totals, and terms — without leaving the page or editing anything.',
    example: 'Before emailing PO-0039 to Quantum Firetech, Preview confirms the 30L suppression system line, $11,990.61 total, and your ABN all render correctly.',
  },
  download: {
    title: 'Download',
    description: 'Saves a PDF of this purchase order to your device. Use it for your own records, to attach to a manual email, or to print a hard copy for the site.',
    example: 'Download PO-0039 as a PDF and file it in the job folder for JOB-27287 before the fitter arrives on site.',
  },
  email: {
    title: 'Email',
    description: 'Opens the email composer with the supplier\'s address pre-filled and the PO PDF attached — the standard way to send an order for confirmation.',
    example: 'Email PO-0039 to accounts@quantumfiretech.com.au; the supplier receives the PDF and can confirm delivery dates from their inbox.',
  },
  copy: {
    title: 'Copy',
    description: 'Duplicates this purchase order into a new document. Choose what to copy it to — Invoice, Quote, Report, Purchase Order, Bill, or Supplier Credit Note — then pick which line types to include (Service, Product, Equipment, Subcontractor service) and whether jobs carry across. A fresh document number is issued; header fields and selected lines come with it.',
    example: 'Copy PO-0039 → Bill, tick Product + Equipment only, and "Copy jobs across" — you get a new bill draft with stock lines and JOB-27287 still linked, but service lines stay on the PO.',
  },
  createJob: {
    title: 'Create job',
    description: 'Creates a field job from this PO\'s lines so your team can schedule and track the work. "Create project jobs" splits one PO across multiple site jobs when a single order covers more than one location.',
    example: 'PO-0039 for a fire-suppression install at Campbellfield — Create job schedules the fitter, copies the product lines, and links the PO so received quantities flow through to the job card.',
  },
  createCredit: {
    title: 'Create Credit',
    description: 'Raises a new supplier credit note against this PO — used when the supplier owes you money back (returns, over-billing, or a credit on account).',
    example: 'Quantum Firetech over-billed a nozzle pack on PO-0039. Create Credit opens a supplier credit note draft linked to this PO for the $184.50 variance.',
  },
  useCredit: {
    title: 'Use Credit',
    description: 'Applies an existing supplier credit note to reduce what you owe on this PO or a linked bill — instead of creating a new credit.',
    example: 'You already have credit note SCN-0041 from last month\'s return. Use Credit applies it to the bill linked to PO-0039, reducing the amount due.',
  },
  exportXero: {
    title: 'Export to Xero',
    description: 'Pushes this purchase order (or its linked bill) to Xero as a draft purchase order or bill so your bookkeeper can reconcile without re-keying.',
    example: 'PO-0039 is finalised — Export to Xero creates a matching draft bill in your Xero org with supplier, lines, and GST already mapped.',
  },
};

const COPY_TO_OPTIONS = ['Invoice', 'Quote', 'Report', 'Purchase Order', 'Bill', 'Supplier Credit Note'] as const;
const COPY_ITEM_TYPES = ['Service', 'Product', 'Equipment', 'Subcontractor service'] as const;

// v1.19.3 — PO document width: 10-column line table (Supplier Code + Internal Code + …)
const PO_DOC_MAX_WIDTH = 1280;
/** Purple gutter around white edit box — matches summary `p: 2` so card aligns with PO title / dates */
const PO_MASTHEAD_EDIT_INSET = 2; // theme spacing units (= 16px); was 8px
const PO_LINES_TABLE_MIN_WIDTH = 1280;

const BILL_TO = {
  name: "Northbridge Diesel & Mechanical",
  address: '14 Diesel Way, Campbellfield VIC 3061',
  abn: '61 987 654 321',
  email: 'accounts@kingydiesel.com.au',
  phone: '(03) 9000 0000',
};

const fmt = (n: number) => `$${n.toFixed(2)}`;

/** 6-dot grip glyph — shared by line-header menu + row drag handles. */
const GripDots: React.FC<{ color?: string; size?: number }> = ({ color = '#9aa0a6', size = 4 }) => (
  <Box sx={{ display: 'inline-flex', flexDirection: 'column', gap: '3px' }}>
    {[0, 1, 2].map(r => (
      <Box key={r} sx={{ display: 'flex', gap: '3px' }}>
        {[0, 1].map(c => (
          <Box key={c} sx={{ width: size, height: size, borderRadius: '50%', backgroundColor: color }} />
        ))}
      </Box>
    ))}
  </Box>
);

// ── Inline text editor (v1.18.1) ─────────────────────────────────────────────
// Click-to-edit text cell used for editable Code / Supplier Code / Description
// on the PO document. Commits on blur or Enter; Esc cancels.
const InlineTextEdit: React.FC<{
  value: string;
  onSave: (v: string) => void;
  placeholder?: string;
  mono?: boolean;
  color?: string;
  fontWeight?: number;
  fontSize?: string;
  uppercase?: boolean;
  display?: string;
  align?: 'left' | 'center' | 'right';
  width?: number | string;
}> = ({ value, onSave, placeholder, mono, color, fontWeight = 400, fontSize = '0.78rem', uppercase, display, align = 'left', width }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  useEffect(() => { setDraft(value); }, [value]);
  if (editing) {
    return (
      <TextField
        value={draft}
        autoFocus
        size="small"
        variant="standard"
        onChange={e => setDraft(uppercase ? e.target.value.toUpperCase() : e.target.value)}
        onBlur={() => { setEditing(false); if (draft !== value) onSave(draft.trim()); }}
        onKeyDown={e => {
          if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
          if (e.key === 'Escape') { setDraft(value); setEditing(false); }
        }}
        InputProps={{ style: { fontFamily: mono ? 'monospace' : 'inherit', fontSize, fontWeight, textAlign: align } }}
        sx={{ minWidth: 56, width: width ?? '100%' }}
      />
    );
  }
  return (
    <Box
      onClick={() => setEditing(true)}
      sx={{
        cursor: 'text', minHeight: 18, textAlign: align,
        fontFamily: mono ? 'monospace' : 'inherit', fontSize, fontWeight,
        color: value ? (color ?? 'inherit') : '#bbb',
        '&:hover': { textDecoration: 'underline dotted', textUnderlineOffset: 3 },
      }}
    >
      {display ?? (value || placeholder || '—')}
    </Box>
  );
};

// Local demo quote used when the AI parse endpoint is unavailable (no key /
// rate-limited / offline) so the drag-drop workflow is always demoable.
const DEMO_PO_QUOTE = {
  supplierName: 'Kingy Steel Supplies P/L',
  lines: [
    { supplierCode: 'STL-RHS-50',  description: 'RHS 50x50x3mm Mild Steel — 6m length',   qty: 12, unitPrice: 42.50 },
    { supplierCode: 'STL-FB-25',   description: 'Flat Bar 25x5mm Mild Steel — 6m length', qty: 8,  unitPrice: 18.75 },
    { supplierCode: 'STL-RBAR-12', description: 'Reinforcing Bar N12 — 6m length',         qty: 20, unitPrice: 14.20 },
    { supplierCode: 'STL-PLT-6',   description: 'Mild Steel Plate 6mm — 1200x1200',        qty: 2,  unitPrice: 168.00 },
  ],
};

export const POPrintPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const {
    purchaseOrders, poLineDetails, suppliers, products, bills, updateBill, updatePurchaseOrder,
    createBill, setBillLineItems, updatePOLineDetails, addProduct, addSupplierCodeMap, addSupplierPriceBookEntries,
    lookupSupplierCode, supplierPriceBook, trackSupplierCodes, internalCodePrefix,
  } = useData();

  // Unallocated-code resolution (v1.17) — which line (by index) is being
  // resolved, plus a walk-through flag that auto-advances to the next
  // unresolved line after each resolution.
  const [resolveIdx, setResolveIdx] = useState<number | null>(null);
  const [resolveWalk, setResolveWalk] = useState(false);

  // ── Inline supplier-quote import (v1.18.1) ─────────────────────────────────
  const [quoteParsing, setQuoteParsing] = useState(false);
  const [quoteDemo, setQuoteDemo] = useState(false);
  const [pendingQuote, setPendingQuote] = useState<{
    documentType?: string;
    supplierName?: string;
    supplierInvoiceNo?: string;
    quoteNumber?: string;
    lines: Array<{ supplierCode?: string; description: string; qty?: number; unitPrice?: number; suggestedInternal?: string; matchMethod?: string }>;
  } | null>(null);
  const [quoteParseError, setQuoteParseError] = useState<string | null>(null);
  const [quoteFile, setQuoteFile] = useState<File | null>(null);
  const [quoteFileUrl, setQuoteFileUrl] = useState<string | null>(null);
  // v1.37 — source-document preview lives INSIDE the shared import modal as a
  // left rail (collapsed thumbnail by default, expandable to a large reading
  // pane), matching the Bill reconcile flow. The earlier on-page "inline"
  // preview is gone so all four document pages share one import control.
  const [poImportPreviewCollapsed, setPoImportPreviewCollapsed] = useState(true);
  /** Expanded PDF pane width — drag the CollapseDivider to resize (bill-reconcile parity). */
  const [poPdfWidth, setPoPdfWidth] = useState(420);
  const [poPdfDragging, setPoPdfDragging] = useState(false);
  const poSplitRef = useRef<HTMLDivElement>(null);
  const poPdfDragRef = useRef({ active: false, startX: 0, startW: 420, raf: 0 });
  const handlePoPdfDragStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    poPdfDragRef.current = { active: true, startX: e.clientX, startW: poPdfWidth, raf: 0 };
    setPoPdfDragging(true);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    const onMove = (ev: MouseEvent) => {
      if (!poPdfDragRef.current.active) return;
      const delta = ev.clientX - poPdfDragRef.current.startX;
      const containerW = poSplitRef.current?.offsetWidth ?? 1100;
      const next = Math.max(280, Math.min(Math.floor(containerW * 0.7), poPdfDragRef.current.startW + delta));
      // Coalesce to one React update per frame — stops layout thrash / zoom jump.
      cancelAnimationFrame(poPdfDragRef.current.raf);
      poPdfDragRef.current.raf = requestAnimationFrame(() => setPoPdfWidth(next));
    };
    const onUp = () => {
      poPdfDragRef.current.active = false;
      cancelAnimationFrame(poPdfDragRef.current.raf);
      setPoPdfDragging(false);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [poPdfWidth]);
  // v1.20 — per-line resolution state in import preview (ladder + batch actions).
  const [previewResolutions, setPreviewResolutions] = useState<Record<number, ImportLineState>>({});

  // v1.52.16 — column key bumped v2 → v3 for FieldInsight Jobs & inventory headings.
  const [colVisible, setColVisible] = useState<Record<string, boolean>>(() =>
    loadColumnVisibility('po-line-columns-v3', PO_LINE_COL_IDS));
  const toggleCol = (id: string) => {
    setColVisible(prev => {
      const next = { ...prev, [id]: prev[id] === false };
      saveColumnVisibility('po-line-columns-v3', next);
      return next;
    });
  };
  const showCol = (id: string) => colVisible[id] !== false;

  // Resolve PO — id is the PO number (e.g. 'PO-4575')
  const po = purchaseOrders.find(p => p.id === id);
  const supplier = po ? suppliers.find(s => s.id === po.supplierId) : null;
  const lines = po ? poLineDetails.filter(l => l.poId === po.id) : [];
  const poLineColSpan =
    1 // grip
    + (showCol('type') ? 1 : 0)
    + (showCol('category') ? 1 : 0)
    + (showCol('internalCode') ? 1 : 0)
    + (trackSupplierCodes && showCol('supplierCode') ? 1 : 0)
    + 1 // description (locked)
    + (showCol('warehouse') ? 1 : 0)
    + 1 // received qty
    + 1 // unused qty
    + 1 // qty
    + 1 // purchase
    + (showCol('tax') ? 1 : 0)
    + 1 // amount
    + 1; // column settings / delete
  const canInternalAutoCreate = (line: typeof lines[number]) => {
    if (!line.description?.trim()) return false;
    if (trackSupplierCodes) return !!(line.supplierCode || '').trim();
    return true;
  };
  const nextInternalCodePreview = peekNextInternalCode(products, internalCodePrefix);

  // Local editing state for received / unused quantities (FI Jobs & inventory)
  const [receivedEdits, setReceivedEdits] = useState<Record<number, string>>({});
  const [unusedEdits, setUnusedEdits] = useState<Record<number, string>>({});

  // Keep edits in sync when lines change (e.g. after reset)
  useEffect(() => { setReceivedEdits({}); setUnusedEdits({}); }, [po?.id]);

  // Save a received qty change for one line
  const handleReceivedChange = (idx: number, raw: string) => {
    setReceivedEdits(prev => ({ ...prev, [idx]: raw }));
  };

  const handleReceivedCommit = (idx: number) => {
    if (!po) return;
    const raw = receivedEdits[idx];
    if (raw === undefined) return; // no change
    const parsed = parseFloat(raw);
    const clamped = isNaN(parsed) ? 0 : Math.max(0, parsed);
    const updatedLines = lines.map((l, i) =>
      i === idx ? { ...l, receivedToDate: clamped } : l
    );
    updatePOLineDetails(po.id, updatedLines);
    setReceivedEdits(prev => { const n = { ...prev }; delete n[idx]; return n; });
    // Auto-update PO status (FieldInsight parity)
    const allReceived = updatedLines.every(l => l.receivedToDate >= l.orderedQty);
    const anyReceived = updatedLines.some(l => l.receivedToDate > 0);
    if (allReceived && updatedLines.length > 0) updatePurchaseOrder(po.id, { status: 'All received' });
    else if (anyReceived) updatePurchaseOrder(po.id, { status: 'Partial received' });
  };

  const handleUnusedChange = (idx: number, raw: string) => {
    setUnusedEdits(prev => ({ ...prev, [idx]: raw }));
  };

  const handleUnusedCommit = (idx: number) => {
    if (!po) return;
    const raw = unusedEdits[idx];
    if (raw === undefined) return;
    const parsed = parseFloat(raw);
    const clamped = isNaN(parsed) ? 0 : Math.max(0, parsed);
    updateLineField(idx, { unusedQty: clamped });
    setUnusedEdits(prev => { const n = { ...prev }; delete n[idx]; return n; });
  };

  const handleDeleteLine = (idx: number) => {
    if (!po) return;
    updatePOLineDetails(po.id, lines.filter((_, i) => i !== idx));
  };

  const handleReceiveAll = () => {
    if (!po || lines.length === 0) return;
    const updatedLines = lines.map(l => ({
      ...l,
      receivedToDate: l.orderedQty,
    }));
    updatePOLineDetails(po.id, updatedLines);
    setReceivedEdits({});
    setUnusedEdits({});
    updatePurchaseOrder(po.id, { status: 'All received' });
  };

  // ── Unallocated-code resolution (v1.17) ────────────────────────────────────
  // Apply a resolved internal code to one PO line; optionally remember the
  // supplier-code -> internal-code mapping for this supplier (per-supplier).
  const handleResolveCode = (idx: number, internalCode: string, remember: boolean) => {
    if (!po) return;
    const target = lines[idx];
    if (!target) return;
    const updatedLines = lines.map((l, i) =>
      i === idx ? { ...l, inventoryCode: internalCode, lineType: 'product' as const } : l
    );
    updatePOLineDetails(po.id, updatedLines);
    if (remember && target.supplierCode && target.supplierCode.trim()) {
      addSupplierCodeMap({ supplierId: po.supplierId, supplierCode: target.supplierCode, internalCode });
    }
    // v1.18 — link the supplier price-book entry to the internal code (upsert).
    if (target.supplierCode && target.supplierCode.trim()) {
      addSupplierPriceBookEntries([{
        supplierId: po.supplierId, supplierCode: target.supplierCode,
        description: target.description, unitPrice: target.unitPrice,
        internalCode, stocked: false, source: 'quote',
      }]);
    }
    // Walk-through: jump to the next still-unresolved line, if any.
    if (resolveWalk) {
      const nextIdx = updatedLines.findIndex((l, i) => i !== idx && needsInternalCode(l));
      if (nextIdx >= 0) { setResolveIdx(nextIdx); return; }
      setResolveWalk(false);
    }
    setResolveIdx(null);
  };

  // ── Editable PO line fields (v1.18.1) ──────────────────────────────────────
  // Patch a single line (by index) and persist. Used by the inline Code /
  // Supplier Code / Description editors on the PO document.
  const updateLineField = (idx: number, patch: Partial<typeof lines[number]>) => {
    if (!po) return;
    const updated = lines.map((l, i) => (i === idx ? { ...l, ...patch } : l));
    updatePOLineDetails(po.id, updated);
  };

  const handleMarkService = (idx: number) => {
    if (!po) return;
    updateLineField(idx, { lineType: 'service', inventoryCode: '' });
    if (resolveWalk) {
      const nextIdx = lines.findIndex((l, i) => i !== idx && needsInternalCode(l));
      if (nextIdx >= 0) { setResolveIdx(nextIdx); return; }
      setResolveWalk(false);
    }
    setResolveIdx(null);
  };

  // Searchable supplier price book for THIS supplier (v1.18.3).
  const bookForSupplier = po ? supplierPriceBook.filter(e => e.supplierId === po.supplierId) : [];

  // Picking a supplier code from the price book auto-fills the rest of the line
  // (description / unit price / internal code) — but only into empty fields so
  // manual edits are never clobbered. Free text just sets the supplier code.
  const handleSupplierCodePick = (idx: number, entry: import('../data/types').SupplierPriceBookEntry | null, raw: string) => {
    const cur = lines[idx];
    if (!cur) return;
    if (entry) {
      updateLineField(idx, {
        supplierCode: entry.supplierCode,
        description: cur.description?.trim() ? cur.description : entry.description,
        unitPrice: cur.unitPrice ? cur.unitPrice : (entry.unitPrice ?? 0),
        inventoryCode: cur.inventoryCode?.trim() ? cur.inventoryCode : (entry.internalCode || ''),
      });
    } else {
      updateLineField(idx, { supplierCode: raw || undefined });
    }
  };

  // ── Line selection + row drag-and-drop (v1.25) ─────────────────────────────
  // Grip (⠿) on row hover: click = select, Shift = range, Ctrl/⌘ = toggle
  // non-consecutive, drag = move all selected rows. Esc clears.
  const [selectedLineIdxs, setSelectedLineIdxs] = useState<number[]>([]);
  const [lineAnchorIdx, setLineAnchorIdx] = useState<number | null>(null);
  const [dragGhost, setDragGhost] = useState<{ x: number; y: number; count: number; label: string } | null>(null);
  const [dropIdx, setDropIdx] = useState<number | null>(null);
  const [dragHelpOpen, setDragHelpOpen] = useState(false);
  const rowRefs = useRef<Array<HTMLTableRowElement | null>>([]);
  const dropIdxRef = useRef<number | null>(null);
  const linesRef = useRef(lines);
  linesRef.current = lines;

  const clearLineSelection = () => {
    setSelectedLineIdxs([]);
    setLineAnchorIdx(null);
  };

  const allLinesSelected = lines.length > 0 && selectedLineIdxs.length === lines.length;
  const toggleSelectAllLines = () => {
    if (allLinesSelected) clearLineSelection();
    else setSelectedLineIdxs(lines.map((_, i) => i));
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') clearLineSelection(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  // Move the selected rows so they sit before original index `insertAt`
  // (insertAt === lines.length appends at the end). Persists via
  // updatePOLineDetails so totals + storage stay consistent.
  const reorderLines = (sel: number[], insertAt: number) => {
    if (!po || sel.length === 0) return;
    const cur = linesRef.current;
    const selSet = new Set(sel);
    const moving = cur.filter((_, i) => selSet.has(i));
    const rest = cur.filter((_, i) => !selSet.has(i));
    const adjusted = cur.slice(0, insertAt).filter((_, i) => !selSet.has(i)).length;
    const next = [...rest.slice(0, adjusted), ...moving, ...rest.slice(adjusted)];
    updatePOLineDetails(po.id, next);
    // Keep the moved block selected at its new position.
    setSelectedLineIdxs(Array.from({ length: moving.length }, (_, k) => adjusted + k));
    setLineAnchorIdx(adjusted);
  };

  const onGripPointerDown = (e: React.PointerEvent, idx: number) => {
    e.preventDefault();
    const start = { x: e.clientX, y: e.clientY };
    const shift = e.shiftKey;
    const meta = e.ctrlKey || e.metaKey;
    let dragging = false;
    let localSel = selectedLineIdxs.includes(idx) ? selectedLineIdxs : [idx];

    const computeDrop = (clientY: number) => {
      const rows = rowRefs.current.slice(0, linesRef.current.length).filter(Boolean) as HTMLTableRowElement[];
      if (rows.length === 0) return null;
      const top = rows[0].getBoundingClientRect().top;
      const bottom = rows[rows.length - 1].getBoundingClientRect().bottom;
      if (clientY < top - 40 || clientY > bottom + 40) return null;
      for (let i = 0; i < rows.length; i++) {
        const b = rows[i].getBoundingClientRect();
        if (clientY < b.top + b.height / 2) return i;
      }
      return rows.length;
    };

    const onMove = (ev: PointerEvent) => {
      const moved = Math.abs(ev.clientX - start.x) + Math.abs(ev.clientY - start.y);
      if (!dragging && moved > 5 && !shift && !meta) {
        dragging = true;
        if (!selectedLineIdxs.includes(idx)) {
          localSel = [idx];
          setSelectedLineIdxs([idx]);
        }
        document.body.style.cursor = 'grabbing';
      }
      if (dragging) {
        const first = linesRef.current[localSel[0]];
        setDragGhost({
          x: ev.clientX, y: ev.clientY, count: localSel.length,
          label: localSel.length === 1
            ? (first?.description || first?.inventoryCode || 'Line item')
            : `${localSel.length} rows selected`,
        });
        const d = computeDrop(ev.clientY);
        dropIdxRef.current = d;
        setDropIdx(d);
      }
    };

    const onUp = () => {
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
      document.body.style.cursor = '';
      if (dragging) {
        if (dropIdxRef.current !== null) reorderLines(localSel, dropIdxRef.current);
        setDragGhost(null);
        setDropIdx(null);
        dropIdxRef.current = null;
        return;
      }
      // Click (no drag) — selection semantics.
      if (meta) {
        setSelectedLineIdxs(prev =>
          prev.includes(idx) ? prev.filter(i => i !== idx) : [...prev, idx].sort((a, b) => a - b));
        setLineAnchorIdx(idx);
      } else if (shift && lineAnchorIdx !== null) {
        const [a, b] = [Math.min(lineAnchorIdx, idx), Math.max(lineAnchorIdx, idx)];
        setSelectedLineIdxs(Array.from({ length: b - a + 1 }, (_, k) => a + k));
      } else {
        setSelectedLineIdxs(prev => (prev.length === 1 && prev[0] === idx) ? [] : [idx]);
        setLineAnchorIdx(idx);
      }
    };

    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp);
  };

  // ── Add a blank PO line manually (v1.18.3) ─────────────────────────────────
  const handleAddLineItem = () => {
    if (!po) return;
    const existing = poLineDetails.filter(l => l.poId === po.id);
    updatePOLineDetails(po.id, [
      ...existing,
      { poId: po.id, inventoryCode: '', lineType: 'product', supplierCode: undefined, description: '', orderedQty: 1, actualQty: 1, receivedToDate: 0, unitPrice: 0, discount: 0 },
    ]);
  };

  // ── Unified code/supplier-code selectors (v1.19) ───────────────────────────
  const categories = Array.from(new Set(products.map(p => p.category).filter(Boolean))).sort();

  // Option lists for the two inline LineCodeSelect cells.
  const internalOptions: LineCodeOption[] = products.map(p => ({
    key: String(p.id), code: p.code, description: p.description,
    meta: `$${p.purchasePrice.toFixed(2)}`,
  }));
  const supplierOptions: LineCodeOption[] = bookForSupplier.map(e => ({
    key: e.id, code: e.supplierCode, description: e.description,
    meta: e.unitPrice != null ? `$${e.unitPrice.toFixed(2)}` : undefined,
    linkedCode: e.internalCode ?? null,
  }));

  // Create-dialog state for the two "+ New" actions (which line triggered it).
  const [addProductIdx, setAddProductIdx] = useState<number | null>(null);
  const [addProductSeed, setAddProductSeed] = useState('');
  const [addSupplierIdx, setAddSupplierIdx] = useState<number | null>(null);
  const [addSupplierSeed, setAddSupplierSeed] = useState('');

  // Pick an existing internal product → link it to the line. Fills the (empty)
  // PO description only as a starting point; the line description stays the
  // supplier-facing one. Remembers the supplier→internal mapping + links the
  // price-book entry so it auto-matches next time.
  const linkInternalToLine = (idx: number, code: string, productDescription?: string) => {
    if (!po) return;
    const cur = lines[idx];
    if (!cur) return;
    const product = products.find(p => p.code === code);
    const patch: Partial<typeof lines[number]> = {
      inventoryCode: code,
      lineType: 'product',
      ...(product?.category && !cur.category ? { category: product.category } : {}),
    };
    if (!cur.description?.trim() && productDescription) patch.description = productDescription;
    updateLineField(idx, patch);
    if (cur.supplierCode && cur.supplierCode.trim()) {
      addSupplierCodeMap({ supplierId: po.supplierId, supplierCode: cur.supplierCode, internalCode: code });
      addSupplierPriceBookEntries([{
        supplierId: po.supplierId, supplierCode: cur.supplierCode,
        description: cur.description || productDescription || '', unitPrice: cur.unitPrice,
        internalCode: code, stocked: false, source: 'quote',
      }]);
    }
  };
  const handlePickInternal = (idx: number, opt: LineCodeOption) => linkInternalToLine(idx, opt.code, opt.description);
  const openCreateInternal = (idx: number, seed: string) => { setAddProductSeed(seed); setAddProductIdx(idx); };
  const handleCreateInternalSave = (draft: AddProductDraft, addAnother: boolean) => {
    const created = handleCreateProduct(draft);
    if (addProductIdx != null) linkInternalToLine(addProductIdx, created.code, created.description);
    if (!addAnother) setAddProductIdx(null);
  };

  // Pick an existing supplier code → fill the supplier side of the line (and the
  // internal code too if the price-book entry is already linked).
  const handlePickSupplier = (idx: number, opt: LineCodeOption) => {
    const entry = bookForSupplier.find(e => e.id === opt.key) ?? null;
    handleSupplierCodePick(idx, entry, opt.code);
  };
  const handleSupplierFreeText = (idx: number, text: string) => updateLineField(idx, { supplierCode: text || undefined });
  const slugSupplierCode = (description: string) => {
    const slug = description.trim().slice(0, 20).replace(/[^A-Za-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
    return (slug || 'ITEM').toUpperCase();
  };
  const handleInternalAutoCreate = (idx: number) => {
    if (!po || !supplier) return;
    const line = lines[idx];
    if (!line?.description?.trim()) return;

    const sc = (line.supplierCode || '').trim();
    // Supplier codes come from the supplier — never auto-generated when the column is visible.
    if (trackSupplierCodes && !sc) return;

    const supplierCodeForMatch = sc || slugSupplierCode(line.description);
    const entry = bookForSupplier.find(e => e.supplierCode.toUpperCase() === supplierCodeForMatch.toUpperCase());
    const result = autoMatchOrCreate({
      supplierCode: supplierCodeForMatch,
      description: line.description,
      supplierName: supplier.name,
      products,
      priceBookEntry: entry,
      remembered: lookupSupplierCode(po.supplierId, supplierCodeForMatch),
      internalCodePrefix,
    });
    if (result.method === 'created') {
      addProduct({
        code: result.internalCode,
        description: line.description,
        category: 'Uncategorised',
        primarySupplier: po.supplierId,
        purchasePrice: line.unitPrice,
        markup: 45,
        stock: 0,
        minStock: 0,
      });
    }
    const updatedLines = lines.map((l, i) =>
      i === idx
        ? {
            ...l,
            ...(sc ? { supplierCode: sc } : {}),
            inventoryCode: result.internalCode,
            lineType: 'product' as const,
          }
        : l
    );
    updatePOLineDetails(po.id, updatedLines);
    if (sc) {
      addSupplierCodeMap({ supplierId: po.supplierId, supplierCode: sc, internalCode: result.internalCode });
      addSupplierPriceBookEntries([{
        supplierId: po.supplierId, supplierCode: sc,
        description: line.description, unitPrice: line.unitPrice,
        internalCode: result.internalCode, stocked: false, source: 'manual',
      }]);
    }
  };
  const openCreateSupplier = (idx: number, seed: string) => { setAddSupplierSeed(seed); setAddSupplierIdx(idx); };
  const handleCreateSupplierSave = (draft: SupplierCodeDraft, addAnother: boolean) => {
    if (!po || addSupplierIdx == null) return;
    // Create the price-book entry UNLINKED (no internal code yet).
    addSupplierPriceBookEntries([{
      supplierId: po.supplierId, supplierCode: draft.supplierCode,
      description: draft.description, unitPrice: draft.unitPrice,
      stocked: false, source: 'manual',
    }]);
    const cur = lines[addSupplierIdx];
    updateLineField(addSupplierIdx, {
      supplierCode: draft.supplierCode,
      description: cur?.description?.trim() ? cur.description : draft.description,
      unitPrice: cur?.unitPrice ? cur.unitPrice : draft.unitPrice,
    });
    if (!addAnother) setAddSupplierIdx(null);
  };

  // ── Supplier-document import → preview modal then apply (v1.21) ────────────
  const classifyRaw = (l: { supplierCode?: string; description: string }): ImportLineState => {
    if (!po || !supplier) return { status: 'new', userAction: 'pending' };
    const sc = (l.supplierCode || '').trim();
    const entry = sc ? bookForSupplier.find(e => e.supplierCode.toUpperCase() === sc.toUpperCase()) : undefined;
    return classifyImportLine({
      supplierCode: sc,
      description: l.description,
      supplierName: supplier.name,
      products,
      priceBookEntry: entry,
      remembered: sc ? lookupSupplierCode(po.supplierId, sc) : undefined,
    });
  };

  // Heuristic pre-fill: an unmatched line with no supplier code and a
  // service-like description (repairs, labour, freight…) defaults to Service.
  const buildLineResolution = (l: { supplierCode?: string; description: string }, _idx: number): ImportLineState => {
    const base = classifyRaw(l);
    if (base.status === 'new' && guessLineType(l) === 'service') {
      return { ...base, status: 'service', userAction: 'service' };
    }
    return base;
  };

  const resolvePreviewInternal = (
    l: { supplierCode?: string; description: string; qty?: number; unitPrice?: number },
    res: ImportLineState,
    mintedInBatch: string[] = [],
  ): { inventoryCode: string; lineType: 'product' | 'service' } => {
    if (res.status === 'service' || res.userAction === 'service') {
      return { inventoryCode: '', lineType: 'service' };
    }
    if (res.status === 'matched' && res.internalCode) {
      return { inventoryCode: res.internalCode, lineType: 'product' };
    }
    if (res.userAction === 'confirmSuggested' && res.suggestedInternal) {
      return { inventoryCode: res.suggestedInternal, lineType: 'product' };
    }
    if (res.userAction === 'autoCreate' && supplier) {
      const sc = (l.supplierCode || '').trim();
      const catalogue = [
        ...products,
        ...mintedInBatch.map(code => ({ code } as { code: string })),
      ];
      const code = generateInternalCode(
        sc || l.description.slice(0, 12).replace(/\s+/g, '-').toUpperCase(),
        supplier.name,
        catalogue as typeof products,
        internalCodePrefix,
      );
      mintedInBatch.push(code);
      addProduct({
        code,
        description: l.description,
        category: 'Uncategorised',
        primarySupplier: po!.supplierId,
        purchasePrice: l.unitPrice ?? 0,
        markup: 45,
        stock: 0,
        minStock: 0,
      });
      return { inventoryCode: code, lineType: 'product' };
    }
    const sc = (l.supplierCode || '').trim();
    const fallback = res.internalCode || res.suggestedInternal
      || (sc && supplier ? autoMatchInternalCode({
        supplierCode: sc, description: l.description, supplierName: supplier.name,
        products, priceBookEntry: bookForSupplier.find(e => e.supplierCode.toUpperCase() === sc.toUpperCase()),
        remembered: lookupSupplierCode(po!.supplierId, sc),
      })?.internalCode : '')
      || '';
    return { inventoryCode: fallback, lineType: 'product' };
  };

  type PoImportCodeMode = 'useSupplierCodes' | 'autoCreate';

  const applyParsedQuote = (
    q: { supplierName?: string; lines: Array<{ supplierCode?: string; description: string; qty?: number; unitPrice?: number }> },
    resolutionsOverride?: Record<number, ImportLineState>,
    codeMode: PoImportCodeMode = 'autoCreate',
  ) => {
    if (!po || !supplier) return;
    const supId = po.supplierId;
    const resolutions = resolutionsOverride ?? previewResolutions;
    const existing = poLineDetails.filter(l => l.poId === po.id);
    const filtered = (q.lines || []).filter(l => (l.supplierCode || '').trim() || (l.description || '').trim());
    const mintedInBatch: string[] = [];
    const createdSupplierAsInternal = new Set<string>();
    const newLines = filtered.map((l, i) => {
      const sc = (l.supplierCode || '').trim();
      const res0 = resolutions[i] ?? buildLineResolution(l, i);
      const isService = res0.status === 'service' || res0.userAction === 'service'
        || guessLineType(l) === 'service';

      // Explicit pre-parse choice: supplier stock code becomes inventory code.
      if (codeMode === 'useSupplierCodes') {
        const lineType: 'product' | 'service' = isService ? 'service' : 'product';
        const inventoryCode = lineType === 'service' ? '' : sc;
        if (inventoryCode && !products.some(p => p.code === inventoryCode) && !createdSupplierAsInternal.has(inventoryCode)) {
          createdSupplierAsInternal.add(inventoryCode);
          addProduct({
            code: inventoryCode,
            description: l.description || '',
            category: 'Uncategorised',
            primarySupplier: po.supplierId,
            purchasePrice: l.unitPrice ?? 0,
            markup: 45,
            stock: 0,
            minStock: 0,
          });
        }
        return {
          poId: po.id,
          inventoryCode,
          lineType,
          supplierCode: trackSupplierCodes ? (sc || undefined) : undefined,
          description: l.description || '',
          orderedQty: l.qty || 1,
          actualQty: l.qty || 1,
          receivedToDate: 0,
          unitPrice: l.unitPrice || 0,
          discount: 0,
        };
      }

      // Auto-create unique PREFIX-N codes for new lines; keep strong matches.
      let res = res0;
      if (res.status === 'new' && res.userAction === 'pending') {
        res = { ...res, userAction: 'autoCreate' };
      }
      const { inventoryCode, lineType } = resolvePreviewInternal(l, res, mintedInBatch);
      return {
        poId: po.id,
        inventoryCode,
        lineType,
        supplierCode: trackSupplierCodes ? (sc || undefined) : undefined,
        description: l.description || '',
        orderedQty: l.qty || 1,
        actualQty: l.qty || 1,
        receivedToDate: 0,
        unitPrice: l.unitPrice || 0,
        discount: 0,
      };
    });
    if (newLines.length === 0) {
      setQuoteParseError('No line items found in that document.');
      return;
    }
    updatePOLineDetails(po.id, [...existing, ...newLines]);
    if (trackSupplierCodes) {
      addSupplierPriceBookEntries(
        newLines.filter(l => l.supplierCode).map(l => ({
          supplierId: supId, supplierCode: l.supplierCode!, description: l.description,
          unitPrice: l.unitPrice, internalCode: l.inventoryCode || undefined,
          stocked: l.lineType !== 'service', source: 'quote' as const,
        }))
      );
      newLines.forEach(l => {
        if (l.supplierCode && l.inventoryCode) {
          addSupplierCodeMap({ supplierId: supId, supplierCode: l.supplierCode, internalCode: l.inventoryCode });
        }
      });
    }
    clearPendingImport();
  };

  const setPreviewAction = (idx: number, action: ImportUserAction) => {
    setPreviewResolutions(prev => {
      const base = prev[idx] ?? buildLineResolution(pendingQuote!.lines[idx], idx);
      if (action === 'service') return { ...prev, [idx]: { ...base, status: 'service', userAction: 'service' } };
      if (action === 'autoCreate') return { ...prev, [idx]: { ...base, status: 'new', userAction: 'autoCreate' } };
      if (action === 'confirmSuggested') return { ...prev, [idx]: { ...base, userAction: 'confirmSuggested', internalCode: base.suggestedInternal } };
      // 'pending' = revert a service line back to product (re-run the ladder without the heuristic).
      if (action === 'pending') return { ...prev, [idx]: classifyRaw(pendingQuote!.lines[idx]) };
      return prev;
    });
  };

  /** Close mapping popup / clear parse state — leave PDF thumb on the page. */
  const clearPendingImport = () => {
    setPendingQuote(null);
    setPreviewResolutions({});
  };

  /** Remove the source document from the PO frame. */
  const clearImportDocument = () => {
    setQuoteFile(null);
    setQuoteDemo(false);
    setPoImportPreviewCollapsed(true);
    setQuoteFileUrl(prev => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  };

  /** Cancel import entirely (modal Cancel) — drop mapping + document. */
  const discardPreview = () => {
    setQuoteParsing(false);
    clearPendingImport();
    clearImportDocument();
  };

  // Count of preview lines still in the 'new' state (no match, no user decision).
  const previewNewCount = pendingQuote
    ? pendingQuote.lines.reduce((n, l, i) => {
        const res = previewResolutions[i] ?? buildLineResolution(l, i);
        return n + (res.status === 'new' && res.userAction === 'pending' ? 1 : 0);
      }, 0)
    : 0;

  const useSupplierCodesAndApply = () => {
    if (!pendingQuote) return;
    applyParsedQuote(pendingQuote, previewResolutions, 'useSupplierCodes');
  };

  const autoCreateInternalCodesAndApply = () => {
    if (!pendingQuote) return;
    applyParsedQuote(pendingQuote, previewResolutions, 'autoCreate');
  };

  const batchPreviewAction = (action: 'autoCreate' | 'service') => {
    if (!pendingQuote) return;
    const next: Record<number, ImportLineState> = { ...previewResolutions };
    pendingQuote.lines.forEach((l, i) => {
      const base = next[i] ?? buildLineResolution(l, i);
      if (base.status === 'new') {
        next[i] = action === 'service'
          ? { ...base, status: 'service', userAction: 'service' }
          : { ...base, userAction: 'autoCreate' };
      }
    });
    setPreviewResolutions(next);
  };

  // Always open pre-parse so the user picks: use supplier codes OR auto-create
  // unique internal codes. PDF thumbnail stays on the main PO frame.
  const showQuotePreview = (q: typeof DEMO_PO_QUOTE, file: File | null, demo: boolean) => {
    const linesIn = q.lines || [];
    const resolutions: Record<number, ImportLineState> = {};
    linesIn.forEach((l, i) => { resolutions[i] = buildLineResolution(l, i); });
    setQuoteDemo(demo);
    if (file) {
      setQuoteFile(file);
      setQuoteFileUrl(prev => {
        if (prev) URL.revokeObjectURL(prev);
        return URL.createObjectURL(file);
      });
    }
    setPreviewResolutions(resolutions);
    const meta = q as {
      documentType?: string;
      supplierName?: string;
      supplierInvoiceNo?: string;
      quoteNumber?: string;
      lines: typeof linesIn;
    };
    setPendingQuote({
      documentType: meta.documentType,
      supplierName: meta.supplierName,
      supplierInvoiceNo: meta.supplierInvoiceNo,
      quoteNumber: meta.quoteNumber,
      lines: linesIn,
    });
    setPoImportPreviewCollapsed(true);
  };

  const parseAndApplyQuote = (file: File) => {
    setQuoteParsing(true); setQuoteDemo(false); setPendingQuote(null); setQuoteParseError(null);
    setQuoteFile(file);
    setQuoteFileUrl(prev => {
      if (prev) URL.revokeObjectURL(prev);
      return URL.createObjectURL(file);
    });
    const fd = new FormData(); fd.append('file', file);
    fetch('/api/parse-quote', { method: 'POST', body: fd })
      .then(async r => {
        const data = await r.json().catch(() => ({}));
        if (!r.ok) {
          // API / billing / offline — demo fallback so workflow stays testable.
          setQuoteParseError(data.friendly ?? data.error ?? `Parse failed (HTTP ${r.status})`);
          setQuoteDemo(true);
          showQuotePreview(DEMO_PO_QUOTE, file, true);
          setQuoteParsing(false);
          return;
        }
        if (data.code) {
          // Structured AI failure (credits, rate limit, etc.) — show reason + demo.
          setQuoteParseError(data.friendly ?? data.error);
          setQuoteDemo(true);
          showQuotePreview(DEMO_PO_QUOTE, file, true);
        } else if (data.error || !data.lines?.length) {
          // Document parsed but no usable lines — show the real reason, no demo.
          setQuoteParseError(data.error ?? 'No line items found on this document');
          setPendingQuote(null);
        } else {
          showQuotePreview(data, file, false);
        }
        setQuoteParsing(false);
      })
      .catch(() => {
        setQuoteParseError('Could not reach the parse service — check your connection.');
        setQuoteDemo(true);
        showQuotePreview(DEMO_PO_QUOTE, file, true);
        setQuoteParsing(false);
      });
  };

  const handleAutoCreateAndMatch = (idx: number) => {
    if (!po || !supplier) return;
    const line = lines[idx];
    if (!line) return;
    const sc = (line.supplierCode || '').trim();
    const entry = sc ? bookForSupplier.find(e => e.supplierCode.toUpperCase() === sc.toUpperCase()) : undefined;
    const result = autoMatchOrCreate({
      supplierCode: sc,
      description: line.description,
      supplierName: supplier.name,
      products,
      priceBookEntry: entry,
      remembered: sc ? lookupSupplierCode(po.supplierId, sc) : undefined,
      internalCodePrefix,
    });
    if (result.method === 'created') {
      addProduct({
        code: result.internalCode,
        description: line.description,
        category: 'Uncategorised',
        primarySupplier: po.supplierId,
        purchasePrice: line.unitPrice,
        markup: 45,
        stock: 0,
        minStock: 0,
      });
    }
    handleResolveCode(idx, result.internalCode, true);
  };


  // Create a new catalog product from the resolve dialog, default markup applied.
  const handleCreateProduct = (draft: AddProductDraft) => addProduct({
    code: draft.code,
    description: draft.description,
    category: draft.category,
    primarySupplier: po?.supplierId ?? '',
    purchasePrice: draft.purchasePrice,
    markup: draft.markup,
    stock: 0,
    minStock: draft.minStock,
  });

  // Bills linked to this PO
  const linkedBills = po ? bills.filter(b => b.linkedPoId === po.id) : [];
  const totalBilledIncGst = linkedBills.reduce((s, b) => s + b.totalIncGst, 0);

  // "Link Existing Bill" dialog
  // "Change Supplier" dialog (Draft POs only)
  const [changeSupOpen, setChangeSupOpen] = useState(false);
  const [pendingSupplierId, setPendingSupplierId] = useState<string | null>(null);

  // Status menu (▾) for manual state override beside the PO ID.
  const [statusAnchor, setStatusAnchor] = useState<HTMLElement | null>(null);

  // "Create job ▾" menu (v1.24 — FieldInsight parity).
  const [createJobAnchor, setCreateJobAnchor] = useState<HTMLElement | null>(null);

  // Copy PO dialog (v1.27.4 — FieldInsight parity).
  const [copyOpen, setCopyOpen] = useState(false);
  const [copyTo, setCopyTo] = useState('');
  const [copyJobs, setCopyJobs] = useState(true);
  const [copyItems, setCopyItems] = useState<string[]>([...COPY_ITEM_TYPES]);

  // v1.24 — FieldInsight parity furniture (Tier 4): tax exclusivity selects
  // above the line table + "Show in PDF" toggle for attachments.
  const [purchasesTax, setPurchasesTax] = useState<'Exclusive' | 'Inclusive'>('Exclusive');
  const [salesTax, setSalesTax] = useState<'Exclusive' | 'Inclusive'>('Exclusive');
  const [showInPdf, setShowInPdf] = useState(false);

  // ── PO body disclosure sections (v1.27.12) — FI inline chevron pattern ─────
  // Replaces the old View/Edit segmented toggle. Details + Line Items are
  // independent expand/collapse panels; Bills + Credit Notes use the same
  // pattern below. Migrates legacy po-print-view-mode-v1 localStorage keys.
  const [detailsExpanded, setDetailsExpandedRaw] = useState<boolean>(() => {
    try {
      const v = localStorage.getItem('po-print-details-expanded-v1');
      if (v === '1' || v === '0') return v === '1';
      const legacy = localStorage.getItem('po-print-view-mode-v1');
      return legacy === 'edit';
    } catch { return false; }
  });
  const [linesExpanded, setLinesExpandedRaw] = useState<boolean>(() => {
    try {
      const v = localStorage.getItem('po-print-lines-expanded-v1');
      if (v === '1' || v === '0') return v === '1';
      const legacy = localStorage.getItem('po-print-view-mode-v1');
      return legacy !== 'edit';
    } catch { return true; }
  });
  const toggleDetailsExpanded = () => {
    const next = !detailsExpanded;
    setDetailsExpandedRaw(next);
    try { localStorage.setItem('po-print-details-expanded-v1', next ? '1' : '0'); } catch {}
  };
  const toggleLinesExpanded = () => {
    const next = !linesExpanded;
    setLinesExpandedRaw(next);
    try { localStorage.setItem('po-print-lines-expanded-v1', next ? '1' : '0'); } catch {}
  };
  const disclosureKeyDown = (toggle: () => void) => (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggle();
    }
  };

  // ── Linked-bills section expand/collapse (v1.10.3) ─────────────────────────
  // Default = collapsed; the heading + count + action buttons are always visible.
  const [billsExpanded, setBillsExpandedRaw] = useState<boolean>(() => {
    try { return localStorage.getItem('po-print-bills-expanded-v1') === '1'; } catch { return false; }
  });
  const [creditNotesExpanded, setCreditNotesExpandedRaw] = useState<boolean>(() => {
    try { return localStorage.getItem('po-print-credit-notes-expanded-v1') === '1'; } catch { return false; }
  });
  const toggleBillsExpanded = () => {
    const next = !billsExpanded;
    setBillsExpandedRaw(next);
    try { localStorage.setItem('po-print-bills-expanded-v1', next ? '1' : '0'); } catch {}
  };

  // ── Reconciliation entry from PO (v1.52.14) ────────────────────────────────
  // PDF preview + side-by-side matcher live only on /bills/:id?mode=reconcile.
  // From the PO we either navigate straight to that page (bill picked / created)
  // or show a lightweight import picker when no bill is selected yet.
  const [reconPickerOpen, setReconPickerOpen] = useState(false);
  const reconSectionRef = useRef<HTMLDivElement | null>(null);
  const expandBillsAndScrollToRecon = () => {
    if (!billsExpanded) {
      setBillsExpandedRaw(true);
      try { localStorage.setItem('po-print-bills-expanded-v1', '1'); } catch {}
    }
    setTimeout(() => reconSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 60);
  };
  const goToReconcile = (billId: string) => {
    navigate(`/bills/${billId}?mode=reconcile`);
  };
  const openReconWorkspace = (billId?: string) => {
    if (billId) {
      goToReconcile(billId);
      return;
    }
    setReconPickerOpen(true);
    expandBillsAndScrollToRecon();
  };
  const closeReconWorkspace = () => {
    setReconPickerOpen(false);
  };
  const toggleCreditNotesExpanded = () => {
    const next = !creditNotesExpanded;
    setCreditNotesExpandedRaw(next);
    try { localStorage.setItem('po-print-credit-notes-expanded-v1', next ? '1' : '0'); } catch {}
  };
  const DISCLOSURE_ROW_MB = 0.35;
  /** Title row → action buttons when a section is expanded (Bills, Credit Notes). */
  const DISCLOSURE_ACTION_GAP = 1.25;

  // v1.48.4 — tiered cascade headers: each level gets its own tint + left accent
  // so Line Items / Bills / Credit Notes scan as distinct bands. Collapsed
  // totals sit flush right (tabular nums) like the Time & Materials pattern.
  const disclosureLevelSx = (level: 1 | 2 | 3) => {
    const accents = { 1: BRAND, 2: '#6a4fc4', 3: '#9b8cc8' } as const;
    const bgs = { 1: '#f3edfc', 2: '#f8f5ff', 3: '#faf9fd' } as const;
    return {
      display: 'flex',
      alignItems: 'center',
      gap: 0.75,
      width: '100%',
      mx: -0.5,
      px: 1.25,
      py: level === 1 ? 0.9 : level === 2 ? 0.8 : 0.7,
      ml: level === 1 ? 0 : level === 2 ? 0.5 : 1,
      borderRadius: 1.5,
      border: '1px solid #e8e2f4',
      borderLeft: `3px solid ${accents[level]}`,
      backgroundColor: bgs[level],
      cursor: 'pointer',
      transition: 'background-color 0.12s ease',
      '&:hover': { backgroundColor: level === 1 ? '#ebe3f9' : level === 2 ? '#f0ebfa' : '#f5f2fa' },
    } as const;
  };

  const disclosureSummarySx = {
    ml: 'auto',
    flexShrink: 0,
    textAlign: 'right' as const,
    fontSize: '0.72rem',
    color: '#666',
    fontVariantNumeric: 'tabular-nums' as const,
    lineHeight: 1.35,
    pr: 0.25,
  };
  const linesExpandedRef = useRef(linesExpanded);
  const printExpandedLinesRef = useRef(false);
  useEffect(() => { linesExpandedRef.current = linesExpanded; }, [linesExpanded]);
  useEffect(() => {
    const onBeforePrint = () => {
      if (!linesExpandedRef.current) {
        printExpandedLinesRef.current = true;
        setLinesExpandedRaw(true);
      } else {
        printExpandedLinesRef.current = false;
      }
    };
    const onAfterPrint = () => {
      if (printExpandedLinesRef.current) {
        setLinesExpandedRaw(false);
        printExpandedLinesRef.current = false;
      }
    };
    window.addEventListener('beforeprint', onBeforePrint);
    window.addEventListener('afterprint', onAfterPrint);
    return () => {
      window.removeEventListener('beforeprint', onBeforePrint);
      window.removeEventListener('afterprint', onAfterPrint);
    };
  }, []);
  // Supplier credit notes linked to this PO (prototype — empty until wired)
  const creditNotes: Array<{ id: string; number: string; status: string; date: string; supplierNoteNo: string; totalInc: number }> = [];
  const totalCreditInc = creditNotes.reduce((s, c) => s + c.totalInc, 0);
  if (!po || !supplier) {
    return (
      <Box sx={{ p: 4 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/purchase-orders-list')}
          sx={{ textTransform: 'none', mb: 2 }}>
          Back to PO List
        </Button>
        <Typography color="error">Purchase order not found: {id}</Typography>
      </Box>
    );
  }

  const subtotal = lines.reduce((s, l) => {
    const base = l.unitPrice * l.orderedQty;
    const disc = l.discount ? base * l.discount / 100 : 0;
    return s + base - disc;
  }, 0);
  const gst   = subtotal * 0.1;
  const total = subtotal + gst;

  const linesFullyReceived = lines.filter(l => l.receivedToDate >= l.orderedQty).length;
  const poDateStr = new Date(po.date).toLocaleDateString('en-AU');
  const deliveryStr = po.deliveryDate
    ? new Date(po.deliveryDate).toLocaleDateString('en-AU')
    : 'On request';
  const receivedProgressColor = lines.length === 0
    ? '#888'
    : linesFullyReceived === lines.length
      ? '#2e7d32'
      : linesFullyReceived > 0
        ? FI_ORANGE
        : '#888';

  // v1.50.7 — four distinct bill actions (Nielsen #4/#8: no two buttons do
  // the same thing):
  //   Reconcile Supplier Invoice → import picker on PO, then full reconcile page
  //   + Find Bill                → search dialog to link an existing bill
  //   + Generate Bill from PO    → draft bill PRE-FILLED from PO lines
  //   + New bill                 → empty bill document page
  const handleNewBill = () => {
    const newId = createBill({ supplierId: po.supplierId, linkedPoId: po.id, jobRef: po.jobRef });
    navigate(`/bills/${newId}`);
  };

  const handleNewBillInRecon = () => {
    const newId = createBill({ supplierId: po.supplierId, linkedPoId: po.id, jobRef: po.jobRef });
    goToReconcile(newId);
  };

  const handleGenerateBillFromPO = () => {
    if (!po) return;
    const newId = createBill({ supplierId: po.supplierId, linkedPoId: po.id, jobRef: po.jobRef });
    const billLines: BillLineItem[] = lines.map((l, i) => {
      const lineTotal = l.orderedQty * l.unitPrice * (1 - (l.discount || 0) / 100);
      return {
        id: i + 1,
        supplierCode: l.supplierCode ?? '',
        internalCode: l.inventoryCode,
        lineType: l.lineType,
        codeMatch: 'exact',
        description: l.description,
        qty: l.orderedQty,
        unitPrice: l.unitPrice,
        gstTreatment: 'ex',
        gstAmount: lineTotal * 0.1,
        lineTotal,
      };
    });
    const subtotal = billLines.reduce((s, l) => s + l.lineTotal, 0);
    const gst = billLines.reduce((s, l) => s + l.gstAmount, 0);
    setBillLineItems(newId, billLines);
    updateBill(newId, {
      processed: true,
      subtotalExGst: subtotal,
      totalGst: gst,
      totalIncGst: subtotal + gst,
    });
    navigate(`/bills/${newId}`);
  };

  // Drop on the PO import picker → new bill + handoff file → full reconcile page.
  const handleReconInvoiceDrop = (file: File) => {
    const newId = createBill({ supplierId: po.supplierId, linkedPoId: po.id, jobRef: po.jobRef });
    setPendingBillImportFile(file);
    goToReconcile(newId);
  };

  // Opens the import picker on the PO (no bill yet). Pick a bill row or drop
  // an invoice to navigate to the dedicated reconcile page.
  const handleSupplierInvoiceReceived = () => {
    if (!po) return;
    openReconWorkspace();
  };

  // Manual state override — every PoStatus selectable from a small ▾ menu
  // beside the PO ID. Lets approvers / admins jump states without working
  // the natural-flow primary button.
  const ALL_STATUSES: PoStatus[] = PO_STATUSES;

  const handleChangeSupplier = (newSupplierId: string) => {
    updatePurchaseOrder(po.id, { supplierId: newSupplierId });
    // Also update any linked bills that were for the old supplier
    linkedBills.forEach(b => {
      if (b.supplierId === po.supplierId) {
        updateBill(b.id, { supplierId: newSupplierId });
      }
    });
    setChangeSupOpen(false);
    setPendingSupplierId(null);
  };

  // Keep the header toolbar (Back · PO# · status · template) aligned to
  // the same column as the PO body so it doesn't float off to the left/side.
  const bodyMaxWidth = PO_DOC_MAX_WIDTH;


  return (
    <>
    <Box sx={{ width: '100%' }}>
      {/*
        ── Simplified header (FieldInsight-pattern, v1.10.2) ─────────────────
        All workflow verbs moved to the bottom action bar. The header now
        carries identity only: Back · ▼ PO# · Status▾.
      */}
      <Box className="no-print" sx={{ maxWidth: bodyMaxWidth, mx: 'auto', width: '100%', flexShrink: 0 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/purchase-orders-list')}
            sx={{ textTransform: 'none', fontSize: '0.78rem', color: '#666',
              '&:hover': { backgroundColor: 'transparent', color: BRAND } }}>
            Back
          </Button>
          <Typography sx={{ fontWeight: 800, fontSize: '1.05rem', color: FI_ORANGE }}>
            {po.id}
          </Typography>
          <StatusChip
            status={po.status}
            onClick={(e) => setStatusAnchor(e.currentTarget as HTMLElement)}
            deleteIcon={<ArrowDropDownIcon sx={{ fontSize: 18 }} />}
            onDelete={(e) => setStatusAnchor(e.currentTarget as HTMLElement)}
            sx={{ height: 26, cursor: 'pointer' }}
          />
          <Tooltip title="Template" arrow>
            <Select
              size="small"
              value={po.template ?? 'Original Purchase Order'}
              onChange={e => updatePurchaseOrder(po.id, { template: e.target.value })}
              sx={{
                fontSize: '0.72rem', height: 28, minWidth: 180,
                '& .MuiSelect-select': { py: 0.5 },
              }}
            >
              {PO_TEMPLATES.map(t => <MenuItem key={t} value={t} sx={{ fontSize: '0.72rem' }}>{t}</MenuItem>)}
            </Select>
          </Tooltip>
        </Stack>

        {/* Tabs (FieldInsight-style) — placeholder, kept for visual parity */}
        <Stack direction="row" spacing={2} alignItems="center" sx={{ borderBottom: '1px solid transparent' }}>
          <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: FI_ORANGE,
            borderBottom: `2px solid ${FI_ORANGE}`, pb: 0.6 }}>
            Jobs & inventory
          </Typography>
          <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#888', pb: 0.6, cursor: 'pointer',
            '&:hover': { color: BRAND } }}>
            History
          </Typography>
        </Stack>

      </Stack>
      </Box>

      {/* Status manual-override menu (▾ on the status pill) */}
      <Menu
        anchorEl={statusAnchor} open={Boolean(statusAnchor)} onClose={() => setStatusAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top',    horizontal: 'left' }}
        PaperProps={{ sx: { minWidth: 200 } }}
      >
        <Box sx={{ px: 1.5, pt: 1, pb: 0.5 }}>
          <Typography sx={{ fontSize: '0.62rem', fontWeight: 800, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Set status
          </Typography>
        </Box>
        {ALL_STATUSES.map(s => {
          const tone = STATUS_TONE[getStatusTone(s)];
          const isCurrent = s === po.status;
          return (
            <MenuItem key={s} selected={isCurrent}
              onClick={() => { updatePurchaseOrder(po.id, { status: s }); setStatusAnchor(null); }}
              sx={{ py: 0.75, px: 1.5, ...(isCurrent && { backgroundColor: tone.bg }) }}>
              <StatusChip status={s} dense />
            </MenuItem>
          );
        })}
      </Menu>

      <Paper sx={{
        maxWidth: (quoteFileUrl || quoteParsing) && !poImportPreviewCollapsed
          ? PO_DOC_MAX_WIDTH + poPdfWidth
          : PO_DOC_MAX_WIDTH,
        mx: 'auto', width: '100%', p: 0,
        border: '1px solid #e0e0e0', backgroundColor: '#fff',
        // No max-width animation while the PDF pane is open — fights drag-resize.
        transition: (quoteFileUrl || quoteParsing) && !poImportPreviewCollapsed
          ? 'none'
          : 'max-width 0.22s ease',
      }} id="print-area">

        {/* Outer-frame split: PDF rail (left, drag-resizable) · PO content (right) */}
        <Box ref={poSplitRef} sx={{ display: 'flex', alignItems: 'stretch' }}>
          {(quoteFileUrl || quoteParsing) && (
            <>
              <Box
                className="no-print"
                sx={{
                  position: 'sticky', top: 12, alignSelf: 'flex-start',
                  zIndex: 2, backgroundColor: '#fff',
                }}
              >
                <DocumentPreviewRail
                  collapsed={poImportPreviewCollapsed}
                  fileName={quoteFile?.name ?? (quoteDemo ? 'Demo document' : 'Document')}
                  fileUrl={quoteFileUrl}
                  fileType={quoteFile?.type}
                  demo={quoteDemo}
                  thumbWidth={COLLAPSE_THUMB_WIDTH}
                  expandedWidth={poPdfWidth}
                  minHeight={560}
                  showZoom
                  isResizing={poPdfDragging}
                  onExpand={() => poImportPreviewCollapsed && setPoImportPreviewCollapsed(false)}
                  onRemove={clearImportDocument}
                />
              </Box>
              <CollapseDivider
                collapsed={poImportPreviewCollapsed}
                onToggle={() => setPoImportPreviewCollapsed(c => !c)}
                onDragStart={handlePoPdfDragStart}
                expandLabel="Expand document preview"
                collapseLabel="Collapse to thumbnail · drag to resize"
                topOffset={28}
              />
            </>
          )}

          <Box sx={{ flex: 1, minWidth: 0, p: 4 }}>

        {/* ── Masthead card — page identity (v1.27.15) ─────────────────────
            Eyebrow + supplier + fact grid. Whole header clickable; expands
            POEditForm inside the card. Replaces the Details disclosure row. */}
        {/* Masthead card — purple shell; summary p:2; edit inset PO_MASTHEAD_EDIT_INSET (v1.28.9) */}
        <Box className="no-print po-masthead-card" sx={{
          backgroundColor: '#faf8ff',
          border: '1px solid #e0d5f5',
          borderRadius: 2,
          overflow: 'hidden',
        }}>
          <Box sx={{ p: 2 }}>
            <Box
              role="button"
              tabIndex={0}
              onClick={toggleDetailsExpanded}
              onKeyDown={disclosureKeyDown(toggleDetailsExpanded)}
              sx={{ cursor: 'pointer', borderRadius: 1, mx: -1, px: 1, py: 0.25,
                '&:hover': { backgroundColor: '#f3edfc' } }}
            >
              <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
                <Typography sx={{ fontSize: '0.62rem', fontWeight: 800, color: BRAND,
                  textTransform: 'uppercase', letterSpacing: 1 }}>
                  Purchase Order
                </Typography>
                <Stack direction="row" alignItems="center" spacing={0.3}>
                  <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: BRAND }}>
                    Edit details
                  </Typography>
                  <ExpandMoreIcon sx={{
                    fontSize: 18,
                    color: BRAND,
                    transform: detailsExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                    transition: 'transform 0.15s ease',
                  }} />
                </Stack>
              </Stack>
              <Typography sx={{ fontSize: '1.05rem', fontWeight: 800, color: '#2a2a2a', mt: 0.3 }}>
                {supplier.name}
              </Typography>
              <Stack direction="row" spacing={3.5} mt={1} flexWrap="wrap" useFlexGap>
                {[
                  ['PO Date', poDateStr],
                  ['Delivery', deliveryStr],
                  ['Reference', po.jobRef || '—'],
                ].map(([label, value]) => (
                  <Box key={label}>
                    <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: '#888',
                      textTransform: 'uppercase', letterSpacing: 0.4 }}>
                      {label}
                    </Typography>
                    <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, mt: 0.1 }}>{value}</Typography>
                  </Box>
                ))}
              </Stack>
            </Box>
          </Box>
          {detailsExpanded && (
            <>
              <Divider sx={{ borderColor: '#e0d5f5' }} />
              <Box sx={{
                px: PO_MASTHEAD_EDIT_INSET,
                pb: PO_MASTHEAD_EDIT_INSET,
                pt: 1.25,
              }}>
                <POEditForm po={po} />
              </Box>
            </>
          )}
        </Box>

        <Divider className="no-print" sx={{ my: 1.25 }} />

        {/* Attach / AI Import — PDF lives on the outer-frame left rail above. */}
        <Box sx={{ mb: 2.5, display: 'flex', flexDirection: 'column', gap: 1 }} className="no-print">
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Button size="small" variant="text"
              onClick={() => alert('Add from library — pick a stored document. Backlog (FieldInsight parity stub).')}
              sx={LINK_BLUE}>
              + Add from library
            </Button>
          </Stack>
          <AttachFileZone
            onFile={f => updatePurchaseOrder(po.id, { attachments: [...(po.attachments ?? []), f.name] })}
          />
          <AiImportBanner
            onFile={parseAndApplyQuote}
            parsing={quoteParsing}
            parsingLabel="Reading the supplier document…"
            subtitle="Drag a supplier quote or invoice here · PDF preview on the left of this page · map codes in the popup when stock codes are on"
          />
          <FormControlLabel
            control={<Checkbox size="small" checked={showInPdf} onChange={e => setShowInPdf(e.target.checked)}
              sx={{ py: 0, '&.Mui-checked': { color: BRAND } }} />}
            label={<Typography sx={{ fontSize: '0.68rem', color: '#888', fontWeight: 600 }}>Show in PDF — include attachments when this PO is printed or emailed</Typography>}
            sx={{ m: 0, ml: 0.2 }}
          />
        </Box>

        {quoteParseError && !pendingQuote && !quoteParsing && (
          <Alert severity="warning" className="no-print" sx={{ mb: 2, fontSize: '0.78rem' }}
            onClose={() => setQuoteParseError(null)}>
            <strong>Could not import:</strong> {quoteParseError}
          </Alert>
        )}

        {/* ── Line Items — rich collapsed summary (v1.27.13) ────────────── */}
        <Box
          className="no-print"
          role="button"
          tabIndex={0}
          onClick={toggleLinesExpanded}
          onKeyDown={disclosureKeyDown(toggleLinesExpanded)}
          sx={{ ...disclosureLevelSx(1), mb: linesExpanded ? 1.5 : DISCLOSURE_ROW_MB }}
        >
          <ExpandMoreIcon sx={{
            fontSize: 20,
            color: BRAND,
            transform: linesExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
            transition: 'transform 0.15s ease',
            flexShrink: 0,
          }} />
          <Typography variant="body2" fontWeight={800} sx={{ color: BRAND, flexShrink: 0 }}>
            Jobs & inventory
          </Typography>
          <Chip
            label={lines.length === 0 ? 'None' : `${lines.length} line${lines.length !== 1 ? 's' : ''}`}
            size="small"
            sx={{ fontSize: '0.65rem', height: 18, fontWeight: 700, flexShrink: 0,
              backgroundColor: lines.length > 0 ? '#e8f5e9' : '#f5f5f5',
              color: lines.length > 0 ? '#2e7d32' : '#888' }}
          />
          <Typography component="span" sx={disclosureSummarySx}>
            <strong style={{ color: BRAND }}>{fmt(total)}</strong> inc GST
            {lines.length > 0 && (
              <> · <span style={{ color: receivedProgressColor, fontWeight: 700 }}>
                {linesFullyReceived} of {lines.length} received
              </span></>
            )}
          </Typography>
        </Box>

        {linesExpanded && (<>
        {/* Letterhead — print-only; PO identity lives in page header on screen */}
        <Box className="print-only" sx={{ mb: 3 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Box>
              <Typography variant="h5" fontWeight={900} sx={{ color: BRAND, letterSpacing: -0.5 }}>
                PURCHASE ORDER
              </Typography>
              <Typography variant="h6" fontWeight={700} sx={{ color: '#333', mt: 0.5 }}>
                {po.id}
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="h6" fontWeight={800} sx={{ color: '#333' }}>
                {BILL_TO.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">{BILL_TO.address}</Typography>
              <Typography variant="body2" color="text.secondary">ABN: {BILL_TO.abn}</Typography>
            </Box>
          </Stack>
        </Box>

        <Divider className="print-only" sx={{ mb: 2 }} />

        {/*
          ── Supplier + date band — print-only (v1.27.13) ─────────────────────
          On screen: supplier/date/ref in Details collapsed summary + POEditForm.
        */}
        <Box className="print-only" sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'flex-start' }}>
          <Box sx={{ minWidth: 220 }}>
            <Stack direction="row" spacing={1} alignItems="center" mb={0.3}>
              <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                Supplier
              </Typography>
              {po.status === 'Draft' && (
                <Chip
                  label="Change"
                  size="small"
                  onClick={() => { setPendingSupplierId(po.supplierId); setChangeSupOpen(true); }}
                  sx={{ fontSize: '0.6rem', height: 16, cursor: 'pointer', backgroundColor: '#fff3e0', color: '#e65100', fontWeight: 700,
                    '&:hover': { backgroundColor: '#ffe0b2' } }}
                  className="no-print"
                />
              )}
            </Stack>
            <Typography sx={{ fontSize: '0.82rem', fontWeight: 700 }}>{supplier.name}</Typography>
            <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>{supplier.address}</Typography>
            <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>{supplier.email} · {supplier.phone}</Typography>
            <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>ABN: {supplier.abn}</Typography>
          </Box>
          <Box sx={{ flex: 1 }} />
          <Box sx={{ display: 'flex', gap: 4 }}>
            {[
              { label: 'PO Date',       value: new Date(po.date).toLocaleDateString('en-AU') },
              { label: 'Delivery Date', value: po.deliveryDate ? new Date(po.deliveryDate).toLocaleDateString('en-AU') : 'On request' },
              { label: 'Reference',     value: po.jobRef },
            ].map(f => (
              <Box key={f.label}>
                <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                  {f.label}
                </Typography>
                <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, mt: 0.2 }}>{f.value}</Typography>
              </Box>
            ))}
          </Box>
          </Box>
        </Box>

        {/* Unallocated codes: inline LineCodeSelect per row — no top banner (v1.27.16) */}

        {/* Lines table */}
        {/* Tax exclusivity — narrow dropdowns (v1.27) */}
        <Stack direction="row" spacing={2.5} justifyContent="flex-end" alignItems="center" mb={0.8} className="no-print">
          {([
            ['Purchases are tax', purchasesTax, setPurchasesTax],
            ['Sales are tax', salesTax, setSalesTax],
          ] as Array<[string, 'Exclusive' | 'Inclusive', (v: 'Exclusive' | 'Inclusive') => void]>).map(([label, value, setter]) => (
            <Stack key={label} direction="row" spacing={0.8} alignItems="center">
              <Typography sx={{ fontSize: '0.66rem', fontWeight: 700, color: '#888' }}>
                {label} <Box component="span" sx={{ color: '#e65100' }}>*</Box>
              </Typography>
              <Select size="small" value={value}
                onChange={e => setter(e.target.value as 'Exclusive' | 'Inclusive')}
                sx={{ fontSize: '0.66rem', height: 26, minWidth: 108,
                  '& .MuiSelect-select': { py: 0.35 } }}>
                <MenuItem value="Exclusive" sx={{ fontSize: '0.66rem' }}>Exclusive</MenuItem>
                <MenuItem value="Inclusive" sx={{ fontSize: '0.66rem' }}>Inclusive</MenuItem>
              </Select>
            </Stack>
          ))}
        </Stack>
        {/* Lines table — wide layout for 10 columns; scroll on narrow viewports */}
        <Box sx={{ overflowX: 'auto', width: '100%', mb: 3 }}>
        <Table size="small" sx={{ minWidth: PO_LINES_TABLE_MIN_WIDTH, width: '100%', tableLayout: 'auto' }}>
          <TableHead>
            <TableRow>
              <TableCell className="no-print" sx={{ ...TABLE_HEAD_CELL, width: 52, px: 0.25, verticalAlign: 'middle' }}>
                <Stack direction="row" alignItems="center" spacing={0}>
                  <Tooltip
                    title={allLinesSelected
                      ? `Clear selection (${selectedLineIdxs.length})`
                      : lines.length > 0 ? `Select all (${lines.length})` : 'Select all'}
                    arrow
                  >
                    <span>
                      <IconButton
                        size="small"
                        disabled={lines.length === 0}
                        onClick={toggleSelectAllLines}
                        sx={{
                          p: 0.5, borderRadius: 1,
                          color: allLinesSelected ? FI_ORANGE : selectedLineIdxs.length > 0 ? FI_ORANGE : '#9aa0a6',
                          backgroundColor: selectedLineIdxs.length > 0 ? '#fff4ea' : 'transparent',
                          '&:hover': { backgroundColor: '#f1f2f4', color: FI_ORANGE },
                        }}
                      >
                        <GripDots color={selectedLineIdxs.length > 0 ? FI_ORANGE : '#9aa0a6'} />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="How line selection works — click for details" arrow>
                    <IconButton
                      size="small"
                      onClick={() => setDragHelpOpen(true)}
                      sx={{
                        width: 18, height: 18, p: 0, fontSize: '0.62rem', fontWeight: 800,
                        color: '#8a9097', borderRadius: '50%',
                        border: '1.5px solid #c9ccd0',
                        '&:hover': { borderColor: FI_ORANGE, color: FI_ORANGE, backgroundColor: '#fff4ea' },
                      }}
                    >
                      ?
                    </IconButton>
                  </Tooltip>
                </Stack>
              </TableCell>
              {([
                ...(showCol('type') ? [['Type', 88, false] as const] : []),
                ...(showCol('category') ? [['Category', 120, false] as const] : []),
                ...(showCol('internalCode') ? [['Inventory Code', 120, false] as const] : []),
                ...(trackSupplierCodes && showCol('supplierCode') ? [['Supplier Code', 120, true] as const] : []),
                ['Description', 180, false],
                ...(showCol('warehouse') ? [['Warehouse', 100, false] as const] : []),
                ['Received qty', 88, false],
                ['Unused qty', 80, false],
                ['Qty', 64, false],
                ['Purchase', 80, false],
                ...(showCol('tax') ? [['Tax', 64, false] as const] : []),
                ['Amount', 80, false],
              ] as Array<[string, number, boolean]>).map(([h, w, isSup]) => (
                <TableCell key={h}
                  sx={{ ...(isSup ? TABLE_HEAD_SUPPLIER : TABLE_HEAD_CELL), minWidth: w }}>
                  {h}
                </TableCell>
              ))}
              <LineTableColumnSettings
                columns={PO_LINE_COLUMN_DEFS}
                visible={colVisible}
                onToggle={toggleCol}
                documentKind="po"
                templateName={po?.template}
                returnTo={po ? `/po-print/${po.id}` : undefined}
              />
            </TableRow>
          </TableHead>
          <TableBody>
            {lines.length === 0 ? (
              <TableRow>
                <TableCell colSpan={poLineColSpan} sx={{ textAlign: 'center', py: 3, color: '#bbb', fontStyle: 'italic', fontSize: '0.8rem' }}>
                  No line items yet — drag a supplier quote or invoice into the panel above.
                </TableCell>
              </TableRow>
            ) : (
              lines.map((line, idx) => {
                const base = line.unitPrice * line.orderedQty;
                const disc = line.discount ? base * line.discount / 100 : 0;
                const lineTotal = base - disc;
                const fullyReceived = line.receivedToDate >= line.orderedQty;
                const partiallyReceived = line.receivedToDate > 0 && !fullyReceived;
                const editVal = receivedEdits[idx] !== undefined ? receivedEdits[idx] : String(line.receivedToDate);
                const unusedVal = unusedEdits[idx] !== undefined
                  ? unusedEdits[idx]
                  : String(line.unusedQty ?? 0);
                const isSelected = selectedLineIdxs.includes(idx);
                const showDropBefore = dropIdx === idx;
                const showDropAfter = dropIdx === lines.length && idx === lines.length - 1;
                const product = products.find(p => p.code === line.inventoryCode);
                const categoryVal = line.category ?? product?.category ?? '';
                const warehouseVal = line.warehouse || 'Warehouse';
                const taxYes = line.taxApplicable !== false;
                const categoryOptions = Array.from(new Set(products.map(p => p.category).filter(Boolean))).sort();
                return (
                  <TableRow key={`${line.inventoryCode}-${idx}`}
                    ref={el => { rowRefs.current[idx] = el; }}
                    sx={{
                      '&:nth-of-type(even)': { backgroundColor: '#fafafa' },
                      backgroundColor: fullyReceived ? '#f1f8e9' : undefined,
                      ...(isSelected && {
                        backgroundColor: '#fff4ea !important',
                        '& td:first-of-type': { boxShadow: `inset 3px 0 0 ${FI_ORANGE}` },
                      }),
                      ...(dragGhost && isSelected && { opacity: 0.4 }),
                      ...(showDropBefore && { '& td': { borderTop: `3px solid ${FI_ORANGE}` } }),
                      ...(showDropAfter && { '& td': { borderBottom: `3px solid ${FI_ORANGE}` } }),
                      '&:hover .row-grip': { opacity: 1 },
                    }}>
                    {/* Grip — drag to reorder, click to select (screen only) */}
                    <TableCell className="no-print" sx={{ width: 34, px: 0.5, py: 0.5 }}>
                      <Tooltip arrow placement="left"
                        title="Drag to move · click to select · Shift: range · ⌘/Ctrl: multi-select · Esc: clear">
                        <Box className="row-grip"
                          onPointerDown={e => onGripPointerDown(e, idx)}
                          sx={{
                            display: 'inline-flex', p: '6px 8px', borderRadius: 1,
                            cursor: 'grab', touchAction: 'none',
                            opacity: isSelected ? 1 : 0, transition: 'opacity .12s, background .12s',
                            '&:hover': { backgroundColor: '#f1f2f4' },
                            '&:active': { cursor: 'grabbing' },
                          }}>
                          <GripDots color={isSelected ? FI_ORANGE : '#9aa0a6'} />
                        </Box>
                      </Tooltip>
                    </TableCell>
                    {showCol('type') && (
                      <TableCell sx={{ minWidth: 88, py: 0.5 }}>
                        <Select
                          size="small"
                          value={line.lineType === 'service' ? 'service' : 'product'}
                          onChange={e => updateLineField(idx, {
                            lineType: e.target.value as 'product' | 'service',
                            ...(e.target.value === 'service' ? { inventoryCode: '' } : {}),
                          })}
                          sx={{ fontSize: '0.72rem', height: 28, minWidth: 84,
                            '& .MuiSelect-select': { py: 0.4 } }}
                        >
                          <MenuItem value="product" sx={{ fontSize: '0.72rem' }}>Product</MenuItem>
                          <MenuItem value="service" sx={{ fontSize: '0.72rem' }}>Service</MenuItem>
                        </Select>
                      </TableCell>
                    )}
                    {showCol('category') && (
                      <TableCell sx={{ minWidth: 120, py: 0.5 }}>
                        <Select
                          size="small"
                          displayEmpty
                          value={categoryVal}
                          onChange={e => updateLineField(idx, { category: e.target.value })}
                          renderValue={v => v
                            ? <Typography sx={{ fontSize: '0.72rem' }}>{v}</Typography>
                            : <Typography sx={{ fontSize: '0.72rem', color: FI_ORANGE, fontWeight: 600 }}>Select category</Typography>}
                          sx={{ fontSize: '0.72rem', height: 28, minWidth: 110,
                            '& .MuiSelect-select': { py: 0.4 },
                            '& fieldset': { borderColor: categoryVal ? '#e0e0e0' : '#ffcc80' } }}
                        >
                          <MenuItem value="" sx={{ fontSize: '0.72rem', color: '#999' }}>
                            <em>Select category</em>
                          </MenuItem>
                          {categoryOptions.map(c => (
                            <MenuItem key={c} value={c} sx={{ fontSize: '0.72rem' }}>{c}</MenuItem>
                          ))}
                        </Select>
                      </TableCell>
                    )}
                    {showCol('internalCode') && (
                    <TableCell sx={{ py: 0.5, minWidth: 150 }}>
                      {line.lineType === 'service' ? (
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                          <Typography sx={{ fontSize: '0.75rem', color: '#bbb' }}>—</Typography>
                          <Tooltip title="Service line (labour) — no code required. Click Type to convert to Product.">
                            <Chip label="Service" size="small"
                              sx={{ height: 18, fontSize: '0.58rem', backgroundColor: '#eceff1', color: '#546e7a' }} />
                          </Tooltip>
                        </Stack>
                      ) : (
                        <LineCodeSelect
                          value={line.inventoryCode}
                          options={internalOptions}
                          onPick={opt => handlePickInternal(idx, opt)}
                          onCreateNew={seed => openCreateInternal(idx, seed)}
                          onAutoCreate={canInternalAutoCreate(line) ? () => handleInternalAutoCreate(idx) : undefined}
                          autoCreateLabel={`Auto-set ${nextInternalCodePreview}`}
                          autoCreatePreview={nextInternalCodePreview}
                          onFreeText={t => updateLineField(idx, { inventoryCode: t, lineType: 'product' })}
                          newLabel="inventory item"
                          accent={FI_ORANGE}
                          uppercase
                          needsAction={needsInternalCode(line)}
                          actionLabel="Select code"
                          placeholder="Search inventory…"
                        />
                      )}
                    </TableCell>
                    )}
                    {trackSupplierCodes && showCol('supplierCode') && (
                      <TableCell sx={{ minWidth: 140 }}>
                        <LineCodeSelect
                          value={line.supplierCode ?? ''}
                          options={supplierOptions}
                          onPick={opt => handlePickSupplier(idx, opt)}
                          onCreateNew={seed => openCreateSupplier(idx, seed)}
                          onFreeText={t => handleSupplierFreeText(idx, t)}
                          newLabel="supplier code"
                          accent="#6a1b9a"
                          uppercase
                          emptyLabel="Add supplier code"
                          placeholder="Search supplier codes…"
                        />
                      </TableCell>
                    )}
                    <TableCell sx={{ minWidth: 160 }}>
                      <InlineTextEdit
                        value={line.description}
                        onSave={v => updateLineField(idx, { description: v })}
                        placeholder="Description" fontSize="0.78rem"
                      />
                    </TableCell>
                    {showCol('warehouse') && (
                      <TableCell sx={{ minWidth: 100, py: 0.5 }}>
                        <Select
                          size="small"
                          value={warehouseVal}
                          onChange={e => updateLineField(idx, { warehouse: e.target.value })}
                          sx={{ fontSize: '0.72rem', height: 28, minWidth: 96,
                            '& .MuiSelect-select': { py: 0.4 } }}
                        >
                          <MenuItem value="Warehouse" sx={{ fontSize: '0.72rem' }}>Warehouse</MenuItem>
                          <MenuItem value="Site" sx={{ fontSize: '0.72rem' }}>Site</MenuItem>
                          <MenuItem value="Van" sx={{ fontSize: '0.72rem' }}>Van</MenuItem>
                        </Select>
                      </TableCell>
                    )}
                    <TableCell align="center" className="no-print" sx={{ minWidth: 72 }}>
                      <TextField
                        value={editVal}
                        onChange={e => handleReceivedChange(idx, e.target.value)}
                        onBlur={() => handleReceivedCommit(idx)}
                        onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                        size="small"
                        inputProps={{
                          style: {
                            textAlign: 'right', padding: '2px 6px', width: 52,
                            fontSize: '0.75rem', fontWeight: 600,
                            color: fullyReceived ? '#2e7d32' : partiallyReceived ? FI_ORANGE : '#555',
                          },
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': { borderColor: fullyReceived ? '#a5d6a7' : partiallyReceived ? '#ffcc80' : '#e0e0e0' },
                            '&:hover fieldset': { borderColor: BRAND },
                            '&.Mui-focused fieldset': { borderColor: BRAND },
                          },
                        }}
                      />
                    </TableCell>
                    <TableCell align="center" className="no-print" sx={{ minWidth: 64 }}>
                      <TextField
                        value={unusedVal}
                        onChange={e => handleUnusedChange(idx, e.target.value)}
                        onBlur={() => handleUnusedCommit(idx)}
                        onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                        size="small"
                        inputProps={{
                          style: { textAlign: 'right', padding: '2px 6px', width: 48, fontSize: '0.75rem', fontWeight: 600 },
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': { borderColor: '#e0e0e0' },
                            '&:hover fieldset': { borderColor: BRAND },
                            '&.Mui-focused fieldset': { borderColor: BRAND },
                          },
                        }}
                      />
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: '0.75rem' }}>
                      <InlineTextEdit
                        value={String(line.orderedQty)}
                        onSave={v => {
                          const q = Math.max(0, parseFloat(v) || 0);
                          updateLineField(idx, { orderedQty: q });
                        }}
                        align="right" width={48} fontSize="0.75rem" fontWeight={600}
                      />
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.75rem' }}>
                      <InlineTextEdit
                        value={String(line.unitPrice)}
                        display={line.unitPrice.toFixed(2)}
                        onSave={v => updateLineField(idx, { unitPrice: Math.max(0, parseFloat(v) || 0) })}
                        width={64} fontSize="0.75rem" align="right"
                      />
                    </TableCell>
                    {showCol('tax') && (
                      <TableCell sx={{ minWidth: 64, py: 0.5 }}>
                        <Select
                          size="small"
                          value={taxYes ? 'YES' : 'NO'}
                          onChange={e => updateLineField(idx, { taxApplicable: e.target.value === 'YES' })}
                          sx={{ fontSize: '0.72rem', height: 28, minWidth: 60,
                            '& .MuiSelect-select': { py: 0.4 } }}
                        >
                          <MenuItem value="YES" sx={{ fontSize: '0.72rem' }}>YES</MenuItem>
                          <MenuItem value="NO" sx={{ fontSize: '0.72rem' }}>NO</MenuItem>
                        </Select>
                      </TableCell>
                    )}
                    <TableCell align="right" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                      {lineTotal.toFixed(2)}
                    </TableCell>
                    <TableCell className="no-print" sx={{ width: 72, px: 0.5, borderBottom: '1px solid #e0e0e0' }}>
                      <Stack direction="row" alignItems="center" spacing={0}>
                        <Tooltip title="Remove line">
                          <IconButton size="small" onClick={() => handleDeleteLine(idx)}
                            sx={{ color: '#c62828', p: 0.4, '&:hover': { backgroundColor: '#ffebee' } }}>
                            <CloseIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
        </Box>

        {/* + Add line item — own row */}
        <Stack direction="row" className="no-print" sx={{ mb: 0.75 }}>
          <Button size="small" variant="text" onClick={handleAddLineItem} sx={LINK_BLUE}>
            + Add line item
          </Button>
        </Stack>

        {/* Selection copy actions — own row between Add line item and + Section */}
        {selectedLineIdxs.length > 0 && (
          <Stack direction="row" spacing={0.6} alignItems="center" useFlexGap flexWrap="wrap"
            className="no-print" sx={{ mb: 1.25 }}>
            {[
              {
                label: `Copy to job (${selectedLineIdxs.length})`,
                icon: <WorkIcon />,
                onClick: () => {
                  if (!po) return;
                  const sel = new Set(selectedLineIdxs);
                  updatePOLineDetails(po.id, lines.map((l, i) => sel.has(i) ? { ...l, copiedToJob: true } : l));
                  clearLineSelection();
                },
              },
              {
                label: `To existing job (${selectedLineIdxs.length})`,
                icon: <WorkOutlineIcon />,
                onClick: () => alert(`Copy ${selectedLineIdxs.length} line(s) to an existing job — backlog (FieldInsight parity stub).`),
              },
              {
                label: `To document (${selectedLineIdxs.length})`,
                icon: <ArticleOutlinedIcon />,
                onClick: () => alert(`Copy ${selectedLineIdxs.length} line(s) to another document — backlog (FieldInsight parity stub).`),
              },
            ].map(b => (
              <Button key={b.label} size="small" disableElevation
                startIcon={b.icon} onClick={b.onClick}
                sx={BTN_GHOST_DOC}>
                {b.label}
              </Button>
            ))}
          </Stack>
        )}

        {/* Line tools row + totals */}
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
          <Stack direction="row" spacing={0.6} alignItems="center" useFlexGap flexWrap="wrap" className="no-print">
            {[
              { label: '+ Section',     onClick: () => alert('+ Section — group lines under a section heading. Backlog v1.11.x') },
              { label: 'Import CSV',    onClick: () => alert('Import CSV — bulk-add lines. Backlog v1.11.x') },
              { label: 'Export CSV',    onClick: () => alert('Export CSV — download line items as CSV. Backlog v1.11.x') },
              { label: 'Refresh pricing', onClick: () => alert('Refresh pricing — pull latest from supplier price book. Backlog v1.11.x') },
            ].map(b => (
              <Button key={b.label} size="small" variant="contained" disableElevation
                onClick={b.onClick}
                sx={BTN_STANDARD}>
                {b.label}
              </Button>
            ))}
          </Stack>

          <Box sx={{ width: 280, flexShrink: 0 }}>
            <Stack direction="row" justifyContent="space-between" py={0.3}>
              <Typography variant="body2" color="text.secondary">Subtotal (ex GST)</Typography>
              <Typography variant="body2" fontWeight={600}>${subtotal.toFixed(2)}</Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between" py={0.3}>
              <Typography variant="body2" color="text.secondary">GST (10%)</Typography>
              <Typography variant="body2" color="text.secondary">${gst.toFixed(2)}</Typography>
            </Stack>
            <Divider sx={{ my: 0.5 }} />
            <Stack direction="row" justifyContent="space-between" py={0.3}>
              <Typography variant="body2" fontWeight={800} sx={{ color: BRAND }}>TOTAL (inc GST)</Typography>
              <Typography variant="body2" fontWeight={800} sx={{ color: BRAND }}>${total.toFixed(2)}</Typography>
            </Stack>
          </Box>
        </Stack>
        </>)}

        {/* ── Bills section ─────────────────────────────────────────────── */}
        <Divider className="no-print" sx={{ my: 1.25 }} />
        <Box className="no-print" sx={{ mb: billsExpanded ? 1.5 : DISCLOSURE_ROW_MB }}>
          <Box
            role="button"
            tabIndex={0}
            onClick={toggleBillsExpanded}
            onKeyDown={disclosureKeyDown(toggleBillsExpanded)}
            sx={{ ...disclosureLevelSx(2), mb: billsExpanded ? DISCLOSURE_ACTION_GAP : DISCLOSURE_ROW_MB }}
          >
            <ExpandMoreIcon sx={{
              fontSize: 20,
              color: '#6a4fc4',
              transform: billsExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
              transition: 'transform 0.15s ease',
              flexShrink: 0,
            }} />
            <Typography variant="body2" fontWeight={800} sx={{ color: '#5a3db0', flexShrink: 0 }}>
              Bills
            </Typography>
            <Chip
              label={linkedBills.length === 0 ? 'None' : `${linkedBills.length} bill${linkedBills.length !== 1 ? 's' : ''}`}
              size="small"
              sx={{ fontSize: '0.65rem', height: 18, fontWeight: 700, flexShrink: 0,
                backgroundColor: linkedBills.length > 0 ? '#e8f5e9' : '#f5f5f5',
                color: linkedBills.length > 0 ? '#2e7d32' : '#888' }}
            />
            {linkedBills.length >= 1 ? (
              <Typography component="span" sx={disclosureSummarySx}>
                <strong style={{ color: BRAND }}>{fmt(totalBilledIncGst)}</strong> billed
                {' / '}<span style={{ color: '#888' }}>{fmt(total)} PO</span>
              </Typography>
            ) : (
              <Typography component="span" sx={{ ...disclosureSummarySx, color: '#aaa', fontStyle: 'italic' }}>
                No bills linked
              </Typography>
            )}
          </Box>

          {billsExpanded && (
            <Stack direction="row" spacing={0.8} alignItems="center" mb={1.2} flexWrap="wrap" useFlexGap>
              <InfoableButton info={FOOTER_BTN_INFO.reconcileInvoice}>
                <Button size="small" variant="contained" disableElevation
                  onClick={handleSupplierInvoiceReceived}
                  sx={BTN_PURPLE}>
                  Reconcile Supplier Invoice
                </Button>
              </InfoableButton>
              <InfoableButton info={FOOTER_BTN_INFO.findBill}>
                <Button size="small" variant="contained" disableElevation
                  onClick={() => navigate(`/po-print/${po.id}/find-bill`)}
                  sx={BTN_STANDARD}>
                  + Find Bill
                </Button>
              </InfoableButton>
              <InfoableButton info={FOOTER_BTN_INFO.generateBillFromPO}>
                <Button size="small" variant="contained" disableElevation
                  onClick={handleGenerateBillFromPO}
                  sx={BTN_STANDARD}>
                  + Generate Bill from PO
                </Button>
              </InfoableButton>
              <InfoableButton info={FOOTER_BTN_INFO.generateBlankBill}>
                <Button size="small" variant="contained" disableElevation
                  onClick={handleNewBill}
                  sx={BTN_STANDARD}>
                  + New bill
                </Button>
              </InfoableButton>
            </Stack>
          )}

          {billsExpanded && (linkedBills.length === 0 ? (
            <Box sx={{ py: 2, textAlign: 'center', border: '1px dashed #e0d5f5', borderRadius: 2, backgroundColor: '#faf8ff' }}>
              <Typography sx={{ fontSize: '0.78rem', color: '#b0a0d8', fontWeight: 600 }}>No bills linked to this PO yet</Typography>
              <Typography sx={{ fontSize: '0.68rem', color: '#ccc', mt: 0.3 }}>
                Drop a supplier invoice on any linked bill to import it, or link an existing one above
              </Typography>
            </Box>
          ) : (
            <Table size="small" sx={{ '& td, & th': { fontSize: '0.72rem' } }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f0ff' }}>
                  {['Bill #', 'Supplier Inv #', 'Date', 'Status', 'Total (inc GST)', 'Billed vs PO', ''].map(h => (
                    <TableCell key={h} sx={{ fontWeight: 700, color: BRAND, fontSize: '0.68rem', py: 0.8 }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {linkedBills.map(bill => {
                  const billPct = total > 0 ? (bill.totalIncGst / total * 100) : 0;
                  return (
                    <TableRow key={bill.id} hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => openReconWorkspace(bill.id)}>
                      <TableCell sx={{ fontWeight: 700, color: BRAND }}>{bill.billNo}</TableCell>
                      <TableCell sx={{ color: '#555' }}>{bill.supplierInvoiceNo || '—'}</TableCell>
                      <TableCell>{bill.date}</TableCell>
                      <TableCell>
                        <StatusChip status={bill.status} dense />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>
                        {bill.totalIncGst > 0 ? fmt(bill.totalIncGst) : <span style={{ color: '#bbb', fontStyle: 'italic' }}>Not yet imported</span>}
                      </TableCell>
                      <TableCell>
                        {bill.totalIncGst > 0 && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ flex: 1, height: 5, borderRadius: 3, backgroundColor: '#e8eef6', overflow: 'hidden', minWidth: 60 }}>
                              <Box sx={{ width: `${Math.min(100, billPct)}%`, height: '100%', backgroundColor: billPct > 100 ? '#c62828' : BRAND, borderRadius: 3 }} />
                            </Box>
                            <Typography sx={{ fontSize: '0.68rem', color: billPct > 100 ? '#c62828' : '#666', fontWeight: billPct > 100 ? 700 : 400 }}>
                              {billPct.toFixed(0)}%
                            </Typography>
                          </Box>
                        )}
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                          <Tooltip title="Open reconciliation page (PDF preview + match)">
                            <Button size="small" variant="outlined" disableElevation
                              onClick={e => { e.stopPropagation(); openReconWorkspace(bill.id); }}
                              sx={{ textTransform: 'none', fontSize: '0.65rem', py: 0.2, px: 0.8, minWidth: 0,
                                borderColor: PO_COLORS.purple, color: PO_COLORS.purple,
                                '&:hover': { borderColor: PO_COLORS.purpleHover, backgroundColor: '#f5f0ff' } }}>
                              Reconcile
                            </Button>
                          </Tooltip>
                          <Tooltip title="Open bill page">
                            <Button size="small" variant="outlined"
                              startIcon={<OpenInNewIcon sx={{ fontSize: 11 }} />}
                              onClick={e => { e.stopPropagation(); navigate(`/bills/${bill.id}`); }}
                              sx={{ textTransform: 'none', fontSize: '0.65rem', borderColor: BRAND, color: BRAND, py: 0.2, px: 0.8, minWidth: 0 }}>
                              Open
                            </Button>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          ))}

          {/* Multi-bill total billed row — only when bills section is expanded */}
          {billsExpanded && linkedBills.length > 1 && (
            <Stack direction="row" justifyContent="flex-end" mt={1} pr={0.5}>
              <Box sx={{ width: 260, border: `1px solid ${BRAND}33`, borderRadius: 1, backgroundColor: '#faf8ff', px: 1.5, py: 0.7 }}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography sx={{ fontSize: '0.7rem', color: '#888' }}>Total billed across {linkedBills.length} bills</Typography>
                  <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: BRAND }}>{fmt(totalBilledIncGst)}</Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography sx={{ fontSize: '0.7rem', color: '#888' }}>PO value</Typography>
                  <Typography sx={{ fontSize: '0.7rem', fontWeight: 600 }}>{fmt(total)}</Typography>
                </Stack>
                {totalBilledIncGst > total && (
                  <Typography sx={{ fontSize: '0.65rem', color: '#c62828', fontWeight: 700, mt: 0.3 }}>
                    ⚠ Total billed exceeds PO value by {fmt(totalBilledIncGst - total)}
                  </Typography>
                )}
              </Box>
            </Stack>
          )}

          {/* ── Reconcile import picker (v1.52.14) — PDF preview lives on
              /bills/:id?mode=reconcile only, not embedded on the PO. */}
          {billsExpanded && reconPickerOpen && (
            <Box ref={reconSectionRef} className="no-print" sx={{ mt: 2, scrollMarginTop: 12 }}>
              <Box sx={{ border: '1px solid #e0d5f5', borderRadius: 2, overflow: 'hidden', backgroundColor: '#fdfcff' }}>
                <Box sx={{ px: 1.5, py: 1, backgroundColor: '#f5f0ff', borderBottom: '1px solid #e0d5f5',
                  display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color: BRAND,
                    textTransform: 'uppercase', letterSpacing: 0.6 }}>
                    Start reconciliation
                  </Typography>
                  <Box sx={{ flex: 1 }} />
                  <Tooltip title="Close" arrow>
                    <IconButton size="small" onClick={closeReconWorkspace} sx={{ color: '#8a7ca8' }}>
                      <CloseIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Box sx={{ p: 2.5 }}>
                  <AiImportBanner
                    variant="hero"
                    onFile={handleReconInvoiceDrop}
                    title="Supplier Invoice Import"
                    subtitle={`Drag the supplier invoice here — AI reads the lines into a new bill and opens reconciliation against ${po.id}`}
                    buttonLabel="Choose invoice"
                  />
                  <Stack direction="row" spacing={1.5} alignItems="center" mt={1.75} flexWrap="wrap" useFlexGap>
                    <Button size="small" variant="contained" disableElevation onClick={handleNewBillInRecon}
                      startIcon={<ReceiptLongIcon sx={{ fontSize: 16 }} />}
                      sx={{ ...BTN_STANDARD, textTransform: 'none' }}>
                      + New blank bill
                    </Button>
                    {linkedBills.length > 0 && (
                      <Typography sx={{ fontSize: '0.72rem', color: '#888' }}>
                        or click a bill in the table above to open its reconciliation page
                      </Typography>
                    )}
                  </Stack>
                </Box>
              </Box>
            </Box>
          )}

          {/* Credit Notes — expand/collapse (v1.27.5, mirrors reconcile section) */}
          <Divider sx={{ my: 1.25 }} />
          <Box>
            <Box
              role="button"
              tabIndex={0}
              onClick={toggleCreditNotesExpanded}
              onKeyDown={disclosureKeyDown(toggleCreditNotesExpanded)}
              sx={{ ...disclosureLevelSx(3), mb: creditNotesExpanded ? DISCLOSURE_ACTION_GAP : DISCLOSURE_ROW_MB }}
            >
              <ExpandMoreIcon sx={{
                fontSize: 20,
                color: '#9b8cc8',
                transform: creditNotesExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                transition: 'transform 0.15s ease',
                flexShrink: 0,
              }} />
              <Typography variant="body2" fontWeight={800} sx={{ color: '#7a6a9e', flexShrink: 0 }}>
                Credit Notes
              </Typography>
              <Chip
                label={creditNotes.length === 0 ? 'None' : `${creditNotes.length} credit${creditNotes.length !== 1 ? 's' : ''}`}
                size="small"
                sx={{ fontSize: '0.65rem', height: 18, fontWeight: 700, flexShrink: 0,
                  backgroundColor: creditNotes.length > 0 ? '#e8f5e9' : '#f5f5f5',
                  color: creditNotes.length > 0 ? '#2e7d32' : '#888' }}
              />
              <Typography component="span" sx={{
                ...disclosureSummarySx,
                fontStyle: creditNotes.length === 0 ? 'italic' : 'normal',
                color: creditNotes.length === 0 ? '#aaa' : '#666',
              }}>
                {creditNotes.length === 0
                  ? 'No supplier credits'
                  : <><strong style={{ color: BRAND }}>{fmt(totalCreditInc)}</strong> credited</>}
              </Typography>
            </Box>

            {creditNotesExpanded && (
              <Stack direction="row" spacing={0.8} alignItems="center" mb={1.2} flexWrap="wrap" useFlexGap>
                <InfoableButton info={FOOTER_BTN_INFO.createCredit}>
                  <Button size="small" variant="contained" disableElevation
                    onClick={() => alert('Create Credit — backlog (FieldInsight parity stub).')}
                    sx={BTN_PURPLE}>
                    Create Credit
                  </Button>
                </InfoableButton>
                <InfoableButton info={FOOTER_BTN_INFO.useCredit}>
                  <Button size="small" variant="contained" disableElevation
                    onClick={() => alert('Use Credit — apply an existing supplier credit to this PO. Backlog (FieldInsight parity stub).')}
                    sx={BTN_STANDARD}>
                    Use Credit
                  </Button>
                </InfoableButton>
              </Stack>
            )}

            {creditNotesExpanded && (creditNotes.length === 0 ? (
              <Box sx={{ py: 1.5, textAlign: 'center', border: '1px dashed #e0d5f5', borderRadius: 2, backgroundColor: '#faf8ff' }}>
                <Typography sx={{ fontSize: '0.78rem', color: '#b0a0d8', fontWeight: 600 }}>No credit notes against this PO</Typography>
              </Box>
            ) : (
              <Table size="small" sx={{ '& td, & th': { fontSize: '0.72rem' } }}>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f0ff' }}>
                    {['Number', 'Status', 'Date', 'Supplier Note #', 'Total (inc)'].map(h => (
                      <TableCell key={h} sx={{ fontWeight: 700, color: BRAND, fontSize: '0.68rem', py: 0.8 }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {creditNotes.map(cn => (
                    <TableRow key={cn.id} hover>
                      <TableCell sx={{ fontWeight: 700, color: BRAND }}>{cn.number}</TableCell>
                      <TableCell><StatusChip status={cn.status} dense /></TableCell>
                      <TableCell>{cn.date}</TableCell>
                      <TableCell sx={{ color: '#555' }}>{cn.supplierNoteNo || '—'}</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>{fmt(cn.totalInc)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ))}
          </Box>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {po.status === 'All received' && (
          <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <CheckCircleIcon sx={{ color: '#2e7d32', fontSize: 16 }} />
              <Typography variant="caption" fontWeight={700} sx={{ color: '#2e7d32' }}>Goods Received</Typography>
            </Box>
          </Stack>
        )}

        {/* Version lives in app Layout footer only — no mid-page prototype stamp. */}

        {/*
          ── Bottom Action Bar — doc tools scroll; lifecycle row sticky below
        */}
        <Box className="no-print" sx={{ mt: 2, pt: 1.5, borderTop: '1px dashed #e0e0e0' }}>
          {/* Row 1 — doc tools (scrolls with document) */}
          <Stack direction="row" spacing={0.8} alignItems="center" mb={1} flexWrap="wrap" useFlexGap>
            <Stack direction="row" spacing={0.8} alignItems="center" useFlexGap>
              <InfoableButton info={FOOTER_BTN_INFO.preview}>
                <Button size="small" disableElevation startIcon={<VisibilityIcon />}
                  onClick={() => alert('Preview — opens read-only PO view in a side panel. Backlog v1.11.x')}
                  sx={BTN_GHOST_DOC}>
                  Preview
                </Button>
              </InfoableButton>
              <InfoableButton info={FOOTER_BTN_INFO.download}>
                <Button size="small" disableElevation startIcon={<DownloadIcon />}
                  onClick={() => window.print()}
                  sx={BTN_GHOST_DOC}>
                  Download
                </Button>
              </InfoableButton>
              <InfoableButton info={FOOTER_BTN_INFO.email}>
                <Button size="small" disableElevation startIcon={<EmailIcon />}
                  onClick={() => alert('Email PO to supplier — backlog v1.11.x')}
                  sx={BTN_GHOST_DOC}>
                  Email
                </Button>
              </InfoableButton>
            </Stack>

            <Box sx={{ width: 24, flexShrink: 0 }} />

            <Stack direction="row" spacing={0.8} alignItems="center" useFlexGap>
              <InfoableButton info={FOOTER_BTN_INFO.copy}>
                <Button size="small" disableElevation startIcon={<ContentCopyIcon />}
                  onClick={() => setCopyOpen(true)}
                  sx={BTN_GHOST_DOC}>
                  Copy
                </Button>
              </InfoableButton>
              <InfoableButton info={FOOTER_BTN_INFO.createJob}>
                <Button size="small" disableElevation startIcon={<WorkIcon />}
                  endIcon={<ExpandMoreIcon sx={{ fontSize: 14 }} />}
                  onClick={e => setCreateJobAnchor(e.currentTarget)}
                  sx={BTN_GHOST_DOC}>
                  Create job
                </Button>
              </InfoableButton>
              <Menu anchorEl={createJobAnchor} open={Boolean(createJobAnchor)} onClose={() => setCreateJobAnchor(null)}>
                <MenuItem onClick={() => { setCreateJobAnchor(null); alert('Create job from this PO — backlog (FieldInsight parity stub).'); }}>
                  Create job
                </MenuItem>
                <MenuItem onClick={() => { setCreateJobAnchor(null); alert('Create project jobs from this PO — backlog (FieldInsight parity stub).'); }}>
                  Create project jobs
                </MenuItem>
              </Menu>
              <InfoableButton info={FOOTER_BTN_INFO.exportXero}>
                <Button size="small" disableElevation startIcon={<OpenInNewIcon />}
                  onClick={() => alert('Export to Xero — backlog (FieldInsight parity stub).')}
                  sx={BTN_GHOST_DOC}>
                  Export to Xero
                </Button>
              </InfoableButton>
            </Stack>
          </Stack>

          {/* Attached files (straight attach, no parsing) — v1.18 */}
          {po.attachments && po.attachments.length > 0 && (
            <Stack direction="row" spacing={0.8} alignItems="center" flexWrap="wrap" useFlexGap className="no-print">
              <Typography sx={{ fontSize: '0.66rem', color: '#888', fontWeight: 700 }}>Attached:</Typography>
              {po.attachments.map((name, i) => (
                <Chip key={`${name}-${i}`} size="small" icon={<AttachFileIcon sx={{ fontSize: 12 }} />}
                  label={name}
                  onDelete={() => updatePurchaseOrder(po.id, { attachments: (po.attachments ?? []).filter((_, j) => j !== i) })}
                  sx={{ fontSize: '0.66rem', height: 22, backgroundColor: '#f5f5f5', '& .MuiChip-icon': { color: '#888' } }} />
              ))}
            </Stack>
          )}

        </Box>

        {/* Row 2 — Lifecycle (sticky: Close · primary · Delete) */}
        <Box className="no-print po-sticky-nav" sx={PO_STICKY_LIFECYCLE_SX}>
            <InfoableButton info={FOOTER_BTN_INFO.close}>
              <Button size="small" disableElevation
                onClick={() => navigate('/purchase-orders-list')}
                sx={BTN_STANDARD}>
                Close
              </Button>
            </InfoableButton>

            <Box sx={{ width: 24, flexShrink: 0 }} />

            {po.status === 'Draft' && (
              <InfoableButton info={FOOTER_BTN_INFO.submit}>
                <Button size="small" variant="contained" disableElevation
                  onClick={() => updatePurchaseOrder(po.id, { status: 'Submitted' })}
                  sx={BTN_STATUS_GREEN}>
                  Submit
                </Button>
              </InfoableButton>
            )}

            {po.status === 'Submitted' && (
              <>
                <InfoableButton info={FOOTER_BTN_INFO.approve}>
                  <Button size="small" variant="contained" disableElevation
                    onClick={() => updatePurchaseOrder(po.id, { status: 'Approved' })}
                    sx={BTN_STATUS_GREEN}>
                    Approve
                  </Button>
                </InfoableButton>
                <InfoableButton info={FOOTER_BTN_INFO.approveEmail}>
                  <Button size="small" disableElevation
                    onClick={() => { updatePurchaseOrder(po.id, { status: 'Approved' }); alert('Email sent to supplier (mock).'); }}
                    sx={BTN_STANDARD}>
                    Approve &amp; Email
                  </Button>
                </InfoableButton>
              </>
            )}

            {(po.status === 'Approved' || po.status === 'Partial received') && (
              <>
                <InfoableButton info={FOOTER_BTN_INFO.finalise}>
                  <Button size="small" variant="contained" disableElevation
                    onClick={() => updatePurchaseOrder(po.id, { status: 'Finalised' })}
                    sx={BTN_STATUS_GREEN}>
                    Finalise
                  </Button>
                </InfoableButton>
                <InfoableButton info={FOOTER_BTN_INFO.receiveAll}>
                  <Button size="small" disableElevation
                    onClick={handleReceiveAll}
                    sx={BTN_STANDARD}>
                    Receive all
                  </Button>
                </InfoableButton>
              </>
            )}

            {(po.status === 'All received' || po.status === 'Bill received') && (
              <InfoableButton info={FOOTER_BTN_INFO.finalise}>
                <Button size="small" variant="contained" disableElevation
                  onClick={() => updatePurchaseOrder(po.id, { status: 'Finalised' })}
                  sx={BTN_STATUS_GREEN}>
                  Finalise
                </Button>
              </InfoableButton>
            )}

            {(po.status === 'Finalised' || po.status === 'Cancelled') && (
              <InfoableButton info={FOOTER_BTN_INFO.reopenDraft}>
                <Button size="small" variant="outlined"
                  onClick={() => updatePurchaseOrder(po.id, { status: 'Draft' })}
                  sx={{ textTransform: 'none', fontSize: '0.74rem', fontWeight: 700,
                    borderColor: '#888', color: '#666', py: 0.55, px: 1.6,
                    '&:hover': { backgroundColor: '#f5f5f5' } }}>
                  Reopen as Draft
                </Button>
              </InfoableButton>
            )}

            <Box sx={PO_STICKY_LIFECYCLE_PUSH} />

            <Box sx={PO_STICKY_LIFECYCLE_TRAIL}>
              <InfoableButton info={FOOTER_BTN_INFO.deletePo}>
                <Button size="small" disableElevation startIcon={<DeleteOutlineIcon sx={{ fontSize: 13 }} />}
                  onClick={() => {
                    const msg = po.status === 'Draft'
                      ? `Delete ${po.id}? This cannot be undone.`
                      : `Delete ${po.id}? This PO is ${po.status}. It will be canceled.`;
                    if (window.confirm(msg)) {
                      updatePurchaseOrder(po.id, { status: 'Cancelled' });
                      navigate('/purchase-orders-list');
                    }
                  }}
                  sx={BTN_GHOST_DANGER}>
                  Delete
                </Button>
              </InfoableButton>
            </Box>
        </Box>

          </Box>{/* end PO content column */}
        </Box>{/* end outer-frame split */}

      </Paper>
    </Box>

    {/* ── Change Supplier Dialog (Draft POs only) ───────────────────── */}
    <Dialog open={changeSupOpen} onClose={() => setChangeSupOpen(false)} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Box sx={{ width: 32, height: 32, borderRadius: '50%', backgroundColor: '#fff3e0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography sx={{ fontSize: '1rem' }}>🏭</Typography>
          </Box>
          <Box>
            <Typography fontWeight={800}>Change Supplier — {po.id}</Typography>
            <Typography variant="caption" color="text.secondary">
              Draft PO only. Changing supplier updates the price book used for this order.
            </Typography>
          </Box>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: '0.72rem', color: '#888', mb: 2 }}>
          Current supplier: <strong style={{ color: BRAND }}>{supplier.name}</strong>
          {lines.length > 0 && (
            <span style={{ color: '#e65100' }}> · {lines.length} line items will remain — review pricing after change</span>
          )}
        </Typography>
        <Stack spacing={1}>
          {suppliers.filter(s => s.id !== po.supplierId).map(s => {
            const isSelected = pendingSupplierId === s.id;
            return (
              <Box key={s.id}
                onClick={() => setPendingSupplierId(s.id)}
                sx={{
                  p: 1.5, border: `2px solid ${isSelected ? BRAND : '#e0e0e0'}`,
                  borderRadius: 2, cursor: 'pointer', backgroundColor: isSelected ? '#f5f0ff' : '#fafafa',
                  transition: 'all 0.15s',
                  '&:hover': { borderColor: BRAND + '80', backgroundColor: '#faf8ff' },
                }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                  <Box>
                    <Stack direction="row" spacing={1} alignItems="center">
                      {isSelected && <CheckCircleIcon sx={{ fontSize: 14, color: BRAND }} />}
                      <Typography sx={{ fontWeight: 800, fontSize: '0.85rem', color: isSelected ? BRAND : '#333' }}>
                        {s.name}
                      </Typography>
                      <Chip label={s.code} size="small"
                        sx={{ fontSize: '0.6rem', height: 16, backgroundColor: '#f5f5f5', color: '#555', fontWeight: 700 }} />
                    </Stack>
                    <Typography sx={{ fontSize: '0.68rem', color: '#888', mt: 0.3 }}>
                      {s.address}
                    </Typography>
                    <Typography sx={{ fontSize: '0.65rem', color: '#aaa' }}>
                      {s.email} · {s.phone} · ABN: {s.abn}
                    </Typography>
                    <Typography sx={{ fontSize: '0.65rem', color: '#888', mt: 0.4 }}>
                      Terms: <strong>{s.terms}</strong> · Default markup: <strong style={{ color: '#e65100' }}>{s.defaultMarkup}%</strong>
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            );
          })}
        </Stack>
        {lines.length > 0 && (
          <Box sx={{ mt: 2, p: 1.5, backgroundColor: '#fff8e1', borderRadius: 2, border: '1px solid #ffe082' }}>
            <Typography sx={{ fontSize: '0.7rem', color: '#e65100', fontWeight: 700 }}>⚠ Review line item pricing</Typography>
            <Typography sx={{ fontSize: '0.65rem', color: '#888', mt: 0.3 }}>
              This PO already has {lines.length} line item{lines.length !== 1 ? 's' : ''}. After changing the supplier,
              review each line's unit price against the new supplier's price book — pricing may differ.
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={() => setChangeSupOpen(false)} sx={{ textTransform: 'none', color: '#666' }}>Cancel</Button>
        <Button
          variant="contained" disableElevation
          disabled={!pendingSupplierId || pendingSupplierId === po.supplierId}
          onClick={() => pendingSupplierId && handleChangeSupplier(pendingSupplierId)}
          sx={{ ...BTN_STATUS_GREEN,
            '&.Mui-disabled': { backgroundColor: '#e0e0e0', color: '#aaa' } }}>
          Confirm Supplier Change
        </Button>
      </DialogActions>
    </Dialog>

    {/* The "Receive Bill" multi-step dialog was removed in v1.10.1 — the
        button now creates the linked draft bill and routes straight to the
        reconciliation view. (See handleSupplierInvoiceReceived above.) */}

    {/* Unallocated-code resolution (v1.17) */}
    {resolveIdx !== null && lines[resolveIdx] && (
      <ResolveCodeDialog
        open={resolveIdx !== null}
        supplierId={po.supplierId}
        supplierName={supplier.name}
        line={{
          supplierCode: lines[resolveIdx].supplierCode ?? '',
          description: lines[resolveIdx].description,
          unitPrice: lines[resolveIdx].unitPrice,
        }}
        products={products}
        internalCodePrefix={internalCodePrefix}
        onClose={() => { setResolveIdx(null); setResolveWalk(false); }}
        onResolved={(internalCode, remember) => handleResolveCode(resolveIdx, internalCode, remember)}
        onCreateProduct={handleCreateProduct}
        onAutoCreateAndMatch={() => handleAutoCreateAndMatch(resolveIdx)}
        onMarkService={() => handleMarkService(resolveIdx)}
      />
    )}

    {/* "+ New inventory item" from the inline Code cell (v1.19) */}
    <AddProductDialog
      open={addProductIdx !== null}
      supplierId={po.supplierId}
      supplierName={supplier.name}
      prefill={{
        // Supplier-agnostic: default to a freshly minted neutral code (or the
        // user's typed seed), never the supplier's own code.
        code: addProductSeed || mintInternalCode(products, internalCodePrefix),
        description: lines[addProductIdx ?? -1]?.description,
        purchasePrice: lines[addProductIdx ?? -1]?.unitPrice,
      }}
      categories={categories}
      onClose={() => setAddProductIdx(null)}
      onSave={handleCreateInternalSave}
    />

    {/* Pre-parse: use supplier codes vs auto-create unique internal codes. */}
    <SupplierImportPreviewDialog
      open={!!pendingQuote}
      lines={pendingQuote?.lines ?? []}
      resolutions={previewResolutions}
      targetLabel={po.id}
      targetKind="po"
      existingLineCount={lines.length}
      trackSupplierCodes={trackSupplierCodes}
      previewNewCount={previewNewCount}
      demo={quoteDemo}
      demoMessage={quoteParseError
        ? `AI parsing unavailable — ${quoteParseError}. Showing demo lines so you can still test the workflow.`
        : 'AI parsing unavailable — showing demo lines. You can still preview and add to this PO.'}
      documentType={pendingQuote?.documentType}
      supplierInvoiceNo={pendingQuote?.supplierInvoiceNo}
      quoteNumber={pendingQuote?.quoteNumber}
      fileName={quoteFile?.name}
      fileUrl={quoteFileUrl}
      fileType={quoteFile?.type}
      previewCollapsed={poImportPreviewCollapsed}
      onTogglePreviewCollapsed={() => setPoImportPreviewCollapsed(c => !c)}
      onSetPreviewAction={setPreviewAction}
      onBatchAction={batchPreviewAction}
      onUseSupplierCodes={useSupplierCodesAndApply}
      onAutoCreateInternalCodes={autoCreateInternalCodesAndApply}
      onDiscard={discardPreview}
    />

    {/* "+ New supplier code" from the inline Supplier Code cell (v1.19) */}
    <AddSupplierCodeDialog
      open={addSupplierIdx !== null}
      supplierName={supplier.name}
      prefill={{
        supplierCode: addSupplierSeed,
        description: lines[addSupplierIdx ?? -1]?.description,
        unitPrice: lines[addSupplierIdx ?? -1]?.unitPrice,
      }}
      onClose={() => setAddSupplierIdx(null)}
      onSave={handleCreateSupplierSave}
    />

    {/* Drag ghost — floating count + label while moving rows (v1.25) */}
    {dragGhost && (
      <Box sx={{
        position: 'fixed', left: dragGhost.x + 14, top: dragGhost.y + 12, zIndex: 2000,
        pointerEvents: 'none', backgroundColor: '#fff', border: `1px solid ${FI_ORANGE}`,
        borderRadius: 2, boxShadow: '0 8px 24px rgba(0,0,0,.18)', px: 2, py: 1,
        fontSize: '0.8rem', color: '#3a3f45', maxWidth: 340, whiteSpace: 'nowrap',
        overflow: 'hidden', textOverflow: 'ellipsis', display: 'flex', alignItems: 'center', gap: 1,
      }}>
        <Box component="span" sx={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          minWidth: 22, height: 22, borderRadius: '11px', backgroundColor: FI_ORANGE,
          color: '#fff', fontWeight: 700, fontSize: '0.72rem', px: 0.7,
        }}>
          {dragGhost.count}
        </Box>
        {dragGhost.label}
      </Box>
    )}

    {/* Copy PO dialog (v1.27.4 — FieldInsight parity) */}
    <Dialog open={copyOpen} onClose={() => setCopyOpen(false)} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography fontWeight={800} sx={{ fontSize: '1.05rem' }}>Copy {po.id}</Typography>
          <IconButton size="small" onClick={() => setCopyOpen(false)} aria-label="Close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: '0.82rem', color: '#555', mb: 2, lineHeight: 1.55 }}>
          Copy this purchase order into a new document. Choose <strong>what to copy it to</strong> — Invoice,
          Quote, Report, Purchase Order, Bill, or Supplier Credit Note — then choose <strong>which item types</strong> to
          include (Service, Product, Equipment, Subcontractor service) and whether jobs carry across.
          All selected information is copied and a new document number is created.
        </Typography>
        <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#888', mb: 0.5 }}>
          Copy to <Box component="span" sx={{ color: '#e65100' }}>*</Box>
        </Typography>
        <Select fullWidth size="small" displayEmpty value={copyTo}
          onChange={e => setCopyTo(e.target.value)}
          sx={{ mb: 2, fontSize: '0.82rem', backgroundColor: '#f7f2fd' }}>
          <MenuItem value="" disabled sx={{ fontSize: '0.82rem', color: '#aaa' }}>----------</MenuItem>
          {COPY_TO_OPTIONS.map(opt => (
            <MenuItem key={opt} value={opt} sx={{ fontSize: '0.82rem' }}>{opt}</MenuItem>
          ))}
        </Select>
        <FormControlLabel
          control={<Checkbox size="small" checked={copyJobs} onChange={e => setCopyJobs(e.target.checked)}
            sx={{ '&.Mui-checked': { color: BRAND } }} />}
          label={<Typography sx={{ fontSize: '0.78rem' }}>Copy jobs across to new document</Typography>}
          sx={{ mb: 2, ml: 0 }}
        />
        <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#888', mb: 0.8 }}>
          Items to copy
        </Typography>
        <Stack spacing={0.3} sx={{ mb: 1.5 }}>
          {COPY_ITEM_TYPES.map(item => (
            <FormControlLabel key={item}
              control={<Checkbox size="small"
                checked={copyItems.includes(item)}
                onChange={e => setCopyItems(prev =>
                  e.target.checked ? [...prev, item] : prev.filter(x => x !== item))}
                sx={{ '&.Mui-checked': { color: BRAND } }} />}
              label={<Typography sx={{ fontSize: '0.78rem' }}>{item}</Typography>}
              sx={{ m: 0 }}
            />
          ))}
        </Stack>
        <Select fullWidth size="small" multiple displayEmpty
          value={copyItems}
          renderValue={sel => (sel as string[]).length ? (sel as string[]).join(', ') : 'Select item types'}
          sx={{ fontSize: '0.78rem', backgroundColor: '#f7f2fd' }}
          readOnly
        >
          {COPY_ITEM_TYPES.map(item => (
            <MenuItem key={item} value={item} sx={{ fontSize: '0.78rem' }}>{item}</MenuItem>
          ))}
        </Select>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'flex-start', gap: 1 }}>
        <Button variant="contained" disableElevation disabled={!copyTo || copyItems.length === 0}
          onClick={() => {
            alert(`Copied ${po.id} → ${copyTo} (${copyItems.join(', ')})${copyJobs ? ' · jobs carried' : ''} — backlog stub.`);
            setCopyOpen(false);
          }}
          sx={BTN_STATUS_ORANGE}>
          Copy
        </Button>
        <Button disableElevation onClick={() => setCopyOpen(false)} sx={BTN_STANDARD}>
          Close
        </Button>
      </DialogActions>
    </Dialog>

    {/* Drag-and-drop help dialog (v1.25) */}
    <Dialog open={dragHelpOpen} onClose={() => setDragHelpOpen(false)} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 800, fontSize: '1rem', pb: 1 }}>Moving line items</DialogTitle>
      <DialogContent>
        {[
          ['Select', 'Click the ⠿ dots to select a row. Click again to deselect.'],
          ['Shift', 'Shift-click the dots to select a consecutive range.'],
          ['Ctrl / ⌘', 'Ctrl- or ⌘-click the dots to add or remove individual rows — non-consecutive selection.'],
          ['Move', 'Drag the dots to move all selected rows to a new position.'],
          ['Copy', 'With rows selected, ghost copy buttons appear beside + Add line item.'],
          ['Deselect', 'Press Esc, or click the header ⠿ again when all rows are selected.'],
        ].map(([k, v]) => (
          <Stack key={k} direction="row" spacing={1.2} alignItems="baseline" sx={{ my: 0.8 }}>
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, minWidth: 64, color: '#3a3f45' }}>{k}</Typography>
            <Typography sx={{ fontSize: '0.8rem', color: '#555' }}>{v}</Typography>
          </Stack>
        ))}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={() => setDragHelpOpen(false)}
          sx={{ textTransform: 'none', fontWeight: 700, color: BRAND }}>
          Got it
        </Button>
      </DialogActions>
    </Dialog>

    </>
  );
};
